import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Check,
  Play,
  RotateCw,
  Download,
  ArrowLeft,
  Bot,
  Hourglass,
  Inbox,
  Pencil,
  X,
  Pause,
  MessageSquare,
  Printer,
  FileText,
  Sparkles,
  Clock,
  Cog,
  Info,
  Trash2,
  ScrollText,
  ShieldCheck,
} from 'lucide-react';
import { Tooltip } from '../components/Tooltip';
import { useConfirm } from '../components/ConfirmDialog';
import { api, apiHref, downloadFile } from '../lib/api-client';
import { ShareButton } from '../components/ShareButton';
import { SkuAutocomplete } from '../components/SkuAutocomplete';
import { MentionAutocomplete } from '../components/MentionAutocomplete';
import { TenderTimeline } from '../components/TenderTimeline';
import { BulkLineActions } from '../components/BulkLineActions';
import { AgentChat } from '../components/AgentChat';
import { FinalizeTenderModal } from '../components/FinalizeTenderModal';
import { PresenceBadge } from '../components/PresenceBadge';
import { PdfSourceViewer } from '../components/PdfSourceViewer';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { useKeyboardShortcuts, type Shortcut } from '../lib/keyboard';
import { extractRunnerQuestions, stageGuidance, isRecord, type RunnerQuestion } from '../lib/runner-questions';
import type {
  ConfidenceLevel,
  ReviewStatus,
  TenderDetail as TenderDetailType,
  TenderLine,
  TenderStatus,
} from '../lib/types';

const PROCESSING_STATUSES: TenderStatus[] = [
  'uploaded',
  'extracting',
  'text_extracted',
  'matching',
  'reviewing',
  'generating_excel',
  'retrying',
];

/**
 * Page /tenders/:id — Revue d'un AO + export Excel.
 *
 * Story : GS-043
 *
 * Pendant le pipeline (statut != ready/exported/failed) : auto-poll 3s.
 * Quand prêt : tableau des lignes par lot, filtres, actions par ligne, export.
 */
/**
 * Sprint 10 : connexion SSE qui pousse `status` (changement statut pipeline),
 * `comment` (nouveau commentaire posté par un autre user) et `review` (autre
 * user a accepté/corrigé une ligne). Plus de polling client.
 */
// ARG Sprint 9 (B-65) — Constraints check (multi-step consistency)
type ConstraintRow = {
  id: number;
  constraint_type: 'budget_total' | 'norme_required' | 'delai_max' | 'exclusion';
  value_json: string;
  raw_text: string | null;
  status: 'respected' | 'violated' | 'unknown';
  validated_at: number | null;
  violation_detail: string | null;
  extracted_at: number;
  extracted_by: string;
};

type ConstraintsResponse = {
  tender_id: string;
  constraints: ConstraintRow[];
  summary: { total: number; respected: number; violated: number; unknown: number };
};

export function TenderDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tender, setTender] = useState<TenderDetailType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [externalEventTick, setExternalEventTick] = useState(0);
  const esRef = useRef<EventSource | null>(null);

  // Message en attente : passe via ?msg=... depuis /upload (Sprint 21+).
  // Stocke en localStorage pour survivre aux refresh pendant que le pipeline
  // tourne (status 'extracting', 'matching', 'reviewing'). Consume = passe au
  // 1er render de l'AgentChat puis clear le storage.
  const [pendingMsg, setPendingMsg] = useState<string | null>(() => {
    if (!id) return null;
    const fromUrl = searchParams.get('msg');
    if (fromUrl) {
      try {
        localStorage.setItem(`tc:pending-msg:${id}`, fromUrl);
      } catch {
        /* ignore (private mode) */
      }
      return fromUrl;
    }
    try {
      return localStorage.getItem(`tc:pending-msg:${id}`);
    } catch {
      return null;
    }
  });

  // Cleanup URL : retire ?msg=... apres consume initial pour ne pas le re-injecter
  // au prochain refresh (le localStorage est notre source de verite jusqu'au consume).
  useEffect(() => {
    if (searchParams.get('msg')) {
      const next = new URLSearchParams(searchParams);
      next.delete('msg');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  function consumePendingMsg() {
    if (!id) return;
    setPendingMsg(null);
    try {
      localStorage.removeItem(`tc:pending-msg:${id}`);
    } catch {
      /* ignore */
    }
  }

  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>(() => Date.now());

  function refresh() {
    if (!id) return Promise.resolve();
    return api
      .get<TenderDetailType>(`/api/tenders/${id}`)
      .then((t) => {
        setTender(t);
        setLastUpdatedAt(Date.now());
      })
      .catch((err) => setError(err.message));
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Polling fallback : si le tender est en cours de traitement, on poll
  // toutes les 5s en plus du SSE. Le SSE peut échouer (CORS split-domain,
  // proxy, network) — le polling garantit qu'on rattrape les transitions.
  useEffect(() => {
    if (!tender) return;
    if (!PROCESSING_STATUSES.includes(tender.statut)) return;
    const intervalId = window.setInterval(() => {
      void refresh();
    }, 5000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tender?.statut]);

  // SSE : ouvre un EventSource tant que le tender n'est pas dans un état terminal
  // post-export ou failed. Reconnecte sur erreur (le navigateur gère auto).
  useEffect(() => {
    if (!id || !tender) return;
    if (tender.statut === 'failed') return;

    // Ferme l'ancien si statut a transitionné
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    // En split-domain (Pages + Workers), il faut un URL absolu vers le Worker —
    // sinon l'EventSource hit Pages qui renvoie le HTML index.html (MIME text/html
    // → "not text/event-stream" → boucle de reconnexion).
    const es = new EventSource(apiHref(`/api/tenders/${id}/stream`), {
      withCredentials: true,
    });
    esRef.current = es;

    es.addEventListener('status', () => {
      void refresh();
    });
    es.addEventListener('comment', () => {
      // un autre user a posté un commentaire : refresh des compteurs uniquement
      setExternalEventTick((t) => t + 1);
    });
    es.addEventListener('review', () => {
      // un autre user a revu une ligne : on refresh tout
      void refresh();
    });
    es.addEventListener('error', () => {
      // EventSource auto-reconnect, on log juste
      console.warn('SSE connection error, browser will retry');
    });

    return () => {
      es.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tender?.statut]);

  if (!tender && !error) return <div className="text-gray-500">Chargement…</div>;
  if (error) return <div className="bg-red-950/30 text-red-300 px-4 py-3 rounded">{error}</div>;
  if (!tender) return null;

  if (PROCESSING_STATUSES.includes(tender.statut)) {
    return (
      <ProcessingView
        tender={tender}
        pendingMsg={pendingMsg}
        onRefresh={refresh}
        lastUpdatedAt={lastUpdatedAt}
      />
    );
  }

  if (tender.statut === 'failed') {
    return <FailedView tender={tender} onChange={refresh} pendingMsg={pendingMsg} />;
  }

  return (
    <ReviewView
      tender={tender}
      onChange={refresh}
      externalEventTick={externalEventTick}
      pendingMsg={pendingMsg}
      consumePendingMsg={consumePendingMsg}
    />
  );
}

// ============================================================
// Processing view
// ============================================================

function ProcessingView({
  tender,
  pendingMsg,
  onRefresh,
  lastUpdatedAt,
}: {
  tender: TenderDetailType;
  pendingMsg: string | null;
  onRefresh: () => Promise<void>;
  lastUpdatedAt: number;
}) {
  const steps: Array<{ key: TenderStatus; label: string }> = [
    { key: 'extracting', label: 'Extraction PDF' },
    { key: 'text_extracted', label: 'Texte extrait' },
    { key: 'matching', label: 'Matching ligne par ligne' },
    { key: 'reviewing', label: 'Vérification cohérence' },
    { key: 'generating_excel', label: 'Generation Excel' },
  ];
  const currentIdx = steps.findIndex((s) => s.key === tender.statut);
  const progress = tender.pipelineProgress;
  const displaySteps =
    progress?.steps ??
    steps.map((s, i) => ({
      key: s.key,
      label: s.label,
      state: i < currentIdx ? ('done' as const) : i === currentIdx ? ('active' as const) : ('pending' as const),
      detail: null,
      at: null,
    }));
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick toutes les secondes pour que "il y a Xs" reste à jour
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const ageSeconds = Math.max(0, Math.floor((now - lastUpdatedAt) / 1000));
  const ageLabel =
    ageSeconds < 5
      ? 'à l\'instant'
      : ageSeconds < 60
        ? `il y a ${ageSeconds}s`
        : `il y a ${Math.floor(ageSeconds / 60)}min ${ageSeconds % 60}s`;
  // Le polling automatique est de 5s. Si > 15s sans update, on signale.
  const stale = ageSeconds > 15;

  async function manualRefresh() {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }

  async function retryStalledPipeline() {
    setRetrying(true);
    setRetryError(null);
    try {
      await api.post(`/api/tenders/${tender.id}/retry`);
      await onRefresh();
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Erreur relance');
    } finally {
      setRetrying(false);
    }
  }

  // Reprise d'un statut transitoire figé APRÈS résolution/corrections : rejoue
  // l'étape depuis les artefacts (garde les réponses/corrections humaines).
  // Contrairement à /retry qui refait tout depuis le PDF.
  const canResumeStalled = ['matching', 'reviewing', 'generating_excel'].includes(tender.statut);
  async function resumeStalledPipeline() {
    setRetrying(true);
    setRetryError(null);
    try {
      await api.post(`/api/tenders/${tender.id}/resume-runner`);
      await onRefresh();
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Erreur reprise');
    } finally {
      setRetrying(false);
    }
  }

  return (
    // Largeur 2xl gardee pour la lecture confort, mais on laisse `mx-auto` pour
    // centrer dans la pleine largeur — evite un saut visuel marque quand le
    // pipeline finit et qu'on bascule sur ReviewView qui occupe toute la largeur.
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-display tracking-tight mb-2">{tender.nom}</h1>
      <p className="text-sm text-steel-400 mb-6">
        Traitement en arrière-plan. Tu peux fermer cet onglet, le pipeline continue côté serveur — tu retrouveras l'AO traité dans le dashboard et tu recevras une notification.
      </p>

      {pendingMsg && (
        <div className="tc-panel p-4 mb-4 border-l-2 border-amber-500">
          <div className="tc-label mb-1 text-amber-400">message en attente</div>
          <p className="text-sm text-steel-200 whitespace-pre-wrap">{pendingMsg}</p>
          <p className="text-2xs text-steel-500 mt-2 font-mono">
            Sera disponible dans la zone de chat dès que le pipeline aura fini.
          </p>
        </div>
      )}

      <div className="tc-panel p-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <div className="tc-label mb-1">avancement</div>
            <div className="text-sm text-steel-300">
              {progress?.steps.find((s) => s.state === 'active')?.label ?? 'Traitement en cours'}
            </div>
          </div>
          <div className="text-xl font-display text-steel-100 tabular-nums">
            {progress?.percent ?? Math.max(5, Math.round((Math.max(currentIdx, 0) / steps.length) * 100))}%
          </div>
        </div>
        <div className="h-2 bg-ink-800 border border-ink-700 mb-6 overflow-hidden">
          <div
            className="h-full bg-brand-500 transition-all duration-500"
            style={{ width: `${progress?.percent ?? 10}%` }}
          />
        </div>
        {stale && (
          <div className="mb-6 border border-amber-900/60 bg-amber-950/30 px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="tc-label mb-1 text-amber-400">traitement bloqué (sans mise à jour)</div>
                <p className="text-sm text-steel-300">
                  {canResumeStalled
                    ? 'Le job semble bloqué. « Reprendre » relance l\'étape en cours en gardant tes réponses/corrections. « Tout refaire » repart du PDF (perd les corrections).'
                    : 'Le job semble bloqué. Relance le traitement pour reprendre depuis les artefacts déjà extraits.'}
                </p>
                {retryError && <p className="text-xs text-red-400 mt-1">{retryError}</p>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {canResumeStalled && (
                  <button
                    onClick={resumeStalledPipeline}
                    disabled={retrying}
                    className="tc-btn-primary text-2xs inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <RotateCw aria-hidden className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} strokeWidth={1.75} />
                    <span>{retrying ? 'Reprise…' : 'Reprendre (garde mes corrections)'}</span>
                  </button>
                )}
                <button
                  onClick={retryStalledPipeline}
                  disabled={retrying}
                  className={`text-2xs inline-flex items-center gap-1.5 disabled:opacity-50 ${canResumeStalled ? 'tc-btn-secondary' : 'tc-btn-primary'}`}
                >
                  <RotateCw aria-hidden className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} strokeWidth={1.75} />
                  <span>{retrying ? 'Relance…' : (canResumeStalled ? 'Tout refaire' : 'Relancer')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        <ol className="space-y-4">
          {displaySteps.map((s, i) => {
            const done = s.state === 'done';
            const active = s.state === 'active';
            const errored = s.state === 'error';
            return (
              <li key={s.key} className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    done
                      ? 'bg-emerald-500 text-white'
                      : errored
                        ? 'bg-red-500 text-white'
                        : active
                        ? 'bg-brand-500 text-ink-950 animate-pulse'
                        : 'bg-ink-700 text-steel-500'
                  }`}
                >
                  {done ? (
                    <Check aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                  ) : errored ? (
                    <X aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                  ) : (
                    i + 1
                  )}
                </div>
                <div className={done ? 'text-steel-500 line-through' : active ? 'font-medium text-steel-100' : 'text-steel-500'}>
                  {s.label}
                  {s.detail && (
                    <div className="text-xs text-steel-500 font-mono truncate mt-0.5 no-underline">
                      {s.detail}
                    </div>
                  )}
                  {active && tender.statut === 'matching' && tender.nbLignes && (
                    <span className="text-xs text-steel-500 ml-2">
                      {tender.nbLignes} lignes à matcher
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {progress?.events?.length ? (
        <div className="tc-panel p-4 mt-4">
          <div className="tc-label mb-3">détails du traitement</div>
          <div className="space-y-2">
            {progress.events.slice(0, 6).map((event) => (
              <div key={`${event.action}-${event.at}`} className="flex items-start gap-3 text-xs">
                <span className="font-mono text-steel-500 whitespace-nowrap">
                  {new Date(event.at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
                <div className="min-w-0">
                  <div className={event.error ? 'text-red-300 font-mono' : 'text-steel-300 font-mono'}>
                    {event.action}
                  </div>
                  {(event.error || event.detail) && (
                    <div className="text-steel-500 font-mono truncate">
                      {event.error || event.detail}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 mt-3">
        <span
          className={`text-xs font-mono ${stale ? 'text-amber-400' : 'text-steel-500'}`}
          aria-live="polite"
        >
          {stale && '⚠ '}
          Dernière mise à jour {ageLabel}
          {!stale && ' · sync auto toutes les 5s'}
        </span>
        {stale && (
          <button
            onClick={retryStalledPipeline}
            disabled={retrying}
            className="tc-btn-ghost text-2xs inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RotateCw
              aria-hidden
              className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`}
              strokeWidth={1.75}
            />
            <span>{retrying ? 'Relance...' : 'Relancer'}</span>
          </button>
        )}
        <button
          onClick={manualRefresh}
          disabled={refreshing}
          className="tc-btn-ghost text-2xs inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <RotateCw
            aria-hidden
            className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
            strokeWidth={1.75}
          />
          <span>Rafraîchir</span>
        </button>
      </div>
      {retryError && (
        <div className="mt-3 border border-red-900/60 bg-red-900/20 text-red-300 px-3 py-2 text-sm font-mono">
          ERROR - {retryError}
        </div>
      )}
    </div>
  );
}

function FailedView({
  tender,
  onChange,
  pendingMsg,
}: {
  tender: TenderDetailType;
  onChange: () => Promise<void>;
  pendingMsg: string | null;
}) {
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Distinguer un AO importe du corpus (jamais traite) d'une vraie erreur pipeline.
  // Marker : prefixe du message d'import inserti par scripts/import-corpus-tenders.ts.
  const isImported = !!tender.errorMessage?.startsWith('Importe du corpus historique');

  async function retry() {
    setRetrying(true);
    setError(null);
    try {
      await api.post(`/api/tenders/${tender.id}/retry`);
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur retry');
      setRetrying(false);
    }
  }

  if (isImported) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="tc-label mb-2">tender / corpus import</div>
        <h1 className="text-2xl font-display tracking-tight mb-2">{tender.nom}</h1>
        {tender.clientFinal && (
          <p className="text-sm text-steel-500 font-mono mb-4">{tender.clientFinal}</p>
        )}

        {pendingMsg && (
          <div className="tc-panel p-4 mt-4 border-l-2 border-amber-500">
            <div className="tc-label mb-1 text-amber-400">message en attente</div>
            <p className="text-sm text-steel-200 whitespace-pre-wrap">{pendingMsg}</p>
            <p className="text-2xs text-steel-500 mt-2 font-mono">
              Sera utilise comme premier message du chat une fois le traitement lance.
            </p>
          </div>
        )}

        <div className="tc-panel p-6 mt-4 border-l-2 border-amber-500">
          <div className="flex items-start gap-3">
            <Inbox aria-hidden className="w-5 h-5 text-amber-500 mt-0.5" strokeWidth={1.75} />
            <div className="flex-1">
              <h2 className="font-display text-base mb-1">Importé du corpus historique</h2>
              <p className="text-sm text-steel-300 leading-relaxed">
                Cet AO a été importé depuis les archives client. Le fichier PDF est sauvegardé
                mais <strong>il n'a pas encore été analysé</strong> par l'agent.
              </p>
              <p className="text-sm text-steel-500 mt-2">
                Cliquer ci-dessous lance l'analyse automatique : extraction des lignes, matching
                avec le catalogue, et vérification de cohérence.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={retry}
            disabled={retrying}
            className="tc-btn-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            {retrying ? (
              <>
                <Hourglass aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                <span>démarrage…</span>
              </>
            ) : (
              <>
                <Play aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                <span>lancer le traitement</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => downloadFile(`/api/tenders/${tender.id}/download?kind=pdf`).catch((e) => setError(e instanceof Error ? e.message : 'Erreur download'))}
            className="text-2xs font-mono uppercase tracking-widest text-steel-300 hover:text-amber-500 inline-flex items-center gap-1.5"
          >
            <Download aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>voir le PDF</span>
          </button>
          <Link
            to="/"
            className="text-2xs font-mono uppercase tracking-widest text-steel-500 hover:text-steel-100 inline-flex items-center gap-1.5"
          >
            <ArrowLeft aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>retour</span>
          </Link>
        </div>
        {error && (
          <div className="mt-3 border border-red-900/60 bg-red-900/20 text-red-300 px-3 py-2 text-sm font-mono">
            ERROR · {error}
          </div>
        )}
      </div>
    );
  }

  // Vraie erreur pipeline (statut failed sans prefix d'import)
  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">{tender.nom}</h1>
      <div className="bg-red-950/30 border border-red-900/50 rounded p-6 mt-4">
        <h2 className="font-semibold text-red-300 mb-2">Le dernier traitement a échoué</h2>
        {tender.errorMessage && (
          <pre className="text-sm text-red-300 whitespace-pre-wrap font-mono">
            {tender.errorMessage}
          </pre>
        )}
        <p className="text-xs text-red-600/80 mt-3">
          Les versions précédentes (si elles existent) restent disponibles ci-dessous.
        </p>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={retry}
          disabled={retrying}
          className="bg-amber-500 hover:bg-amber-400 text-ink-950 px-4 py-2 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {retrying ? (
            'Relance…'
          ) : (
            <>
              <RotateCw aria-hidden className="w-4 h-4" strokeWidth={1.75} />
              <span>Relancer le pipeline</span>
            </>
          )}
        </button>
        <Link to="/" className="text-primary-600 hover:underline text-sm inline-flex items-center gap-1.5">
          <ArrowLeft aria-hidden className="w-4 h-4" strokeWidth={1.75} />
          <span>Retour</span>
        </Link>
      </div>
      {error && <div className="mt-3 bg-red-950/30 text-red-300 px-3 py-2 rounded text-sm">{error}</div>}

      {/* Même en cas d'échec du dernier traitement, on garde l'accès aux
          versions précédentes (téléchargement Excel / lignes par version). */}
      <div className="mt-6">
        <AttemptsHistoryPanel tenderId={tender.id} />
      </div>
    </div>
  );
}

// ============================================================
// Review view (statut = ready / reviewed / exported)
// ============================================================

type ConfFilter = ConfidenceLevel | 'all';
type StatusFilter = ReviewStatus | 'all';

function ReviewView({
  tender,
  onChange,
  externalEventTick,
  pendingMsg,
  consumePendingMsg,
}: {
  tender: TenderDetailType;
  onChange: () => Promise<void>;
  externalEventTick: number;
  pendingMsg: string | null;
  consumePendingMsg: () => void;
}) {
  const [confFilter, setConfFilter] = useState<ConfFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [exporting, setExporting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [bulking, setBulking] = useState(false);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  // ARG Sprint 9 (B-65) — Constraints check banner state
  const [constraints, setConstraints] = useState<ConstraintsResponse | null>(null);

  useEffect(() => {
    api
      .get<ConstraintsResponse>(`/api/tenders/${tender.id}/constraints`)
      .then(setConstraints)
      .catch(() => setConstraints(null));
  }, [tender.id, externalEventTick]);
  const { confirm, dialog } = useConfirm();
  const [showPdf, setShowPdf] = useState(false);
  const [pdfPage, setPdfPage] = useState<number | null>(null);
  // ARG Sprint 9 (B-67) — Hint affiché dans le header `PdfSourceViewer` :
  // designation AO de la ligne sélectionnée + bbox optionnelle V2 (= ignorée
  // V1, l'iframe natif ne supporte pas l'overlay pixel).
  const [pdfSelectedHint, setPdfSelectedHint] = useState<{
    designation: string | null;
    bbox: { x?: number; y?: number; w?: number; h?: number } | null;
  } | null>(null);
  const [focusedLineIdx, setFocusedLineIdx] = useState(0);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showRunnerArtifacts, setShowRunnerArtifacts] = useState(
    tender.statut === 'awaiting_params_review'
      || tender.statut === 'awaiting_references_review'
      || tender.statut === 'awaiting_final_review',
  );
  // Si un message en attente arrive depuis /upload, ouvre directement le chat.
  const [showAgentChat, setShowAgentChat] = useState<boolean>(!!pendingMsg);

  // Reprend la derniere conversation `tender` active si elle existe.
  // - undefined : pas encore fetch
  // - null      : aucune → on en cree une nouvelle au 1er envoi
  // - string    : id a reprendre
  const [tenderConvId, setTenderConvId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!showAgentChat) return;
    if (tenderConvId !== undefined) return; // deja fetch
    api
      .get<{ items: Array<{ id: string }> }>(
        `/api/agent/conversations?contextKind=tender&tenderId=${tender.id}&status=active&limit=1`,
        { domainSlug: undefined },
      )
      .then((d) => setTenderConvId(d.items?.[0]?.id ?? null))
      .catch(() => setTenderConvId(null));
  }, [showAgentChat, tenderConvId, tender.id]);
  const [showFinalize, setShowFinalize] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    api
      .get<{ counts: Record<string, number> }>(`/api/tenders/${tender.id}/comments-counts`)
      .then((d) => setCommentCounts(d.counts ?? {}))
      .catch(() => {});
  }, [tender.id, tender.lines.length, externalEventTick]);

  // ARG Sprint 9 (B-67) — Le scroll iframe à la page est maintenant géré
  // dans `PdfSourceViewer` (= encapsulation propre + viewer réutilisable).
  // L'ancien `useEffect` inline a été supprimé.

  /**
   * ARG B-07 — bulk "tout vérifier" : POST confirm sur toutes les lignes
   * non encore CONFIRMED. Inclut les lignes sans cInfo (D5) — l'idée est de
   * laisser l'utilisateur signer une session de revue d'un coup. Boucle
   * côté frontend (pas d'endpoint batch dédié en Sprint 1).
   */
  async function bulkConfirmArg() {
    const targets = tender.lines.filter(
      (l) => l.infoOutcomeStatus !== 'INFO_CONFIRMED_SUCCESS',
    );
    if (targets.length === 0) return;
    const ok = await confirm({
      title: `Marquer ${targets.length} ligne(s) comme vérifiée(s) ?`,
      message:
        'Bascule toutes les lignes non confirmées en INFO_CONFIRMED_SUCCESS. Action tracée par utilisateur dans l\'audit log.',
      confirmLabel: 'Tout vérifier',
    });
    if (!ok) return;
    setBulking(true);
    try {
      const concurrency = 5;
      for (let i = 0; i < targets.length; i += concurrency) {
        await Promise.all(
          targets.slice(i, i + concurrency).map((l) =>
            api.post(`/api/tenders/${tender.id}/lines/${l.id}/confirm`).catch(() => undefined),
          ),
        );
      }
      await onChange();
    } catch (err) {
      console.error('Bulk ARG confirm failed', err);
    } finally {
      setBulking(false);
    }
  }

  async function bulkAcceptHaute() {
    const targets = tender.lines.filter(
      (l) => l.niveauConfiance === 'haute' && l.statutRevue === 'pending',
    );
    if (targets.length === 0) return;
    const ok = await confirm({
      title: `Accepter ${targets.length} ligne(s) en confiance haute ?`,
      message: 'Toutes les lignes en haute confiance seront marquées "accepted" en lot.',
      confirmLabel: 'Accepter',
    });
    if (!ok) return;
    setBulking(true);
    try {
      // Limite la concurrence pour ne pas DoS l'API D1
      const concurrency = 5;
      for (let i = 0; i < targets.length; i += concurrency) {
        await Promise.all(
          targets.slice(i, i + concurrency).map((l) =>
            api.patch(`/api/tenders/${tender.id}/lines/${l.id}`, { action: 'accept' }),
          ),
        );
      }
      await onChange();
    } catch (err) {
      console.error('Bulk accept failed', err);
    } finally {
      setBulking(false);
    }
  }

  const filtered = useMemo(() => {
    return tender.lines.filter((l) => {
      if (confFilter !== 'all' && l.niveauConfiance !== confFilter) return false;
      if (statusFilter !== 'all' && l.statutRevue !== statusFilter) return false;
      return true;
    });
  }, [tender.lines, confFilter, statusFilter]);

  const lots = useMemo(() => {
    const groups: Record<string, TenderLine[]> = {};
    for (const l of filtered) {
      const key = l.lot ?? 'Sans lot';
      groups[key] ??= [];
      groups[key].push(l);
    }
    return groups;
  }, [filtered]);

  async function downloadExcel() {
    setExporting(true);
    setExportError(null);
    try {
      // Téléchargement direct, disponible dès qu'un Excel existe : brouillon si
      // des points restent à traiter (lignes sans produit surlignées en jaune),
      // version finale sinon. Via fetch+blob (cross-origin friendly X-Dev-Bypass).
      await downloadFile(`/api/tenders/${tender.id}/download?kind=excel`);
      // Best-effort, non bloquant : si tout est traité, marque l'AO comme exporté
      // (cycle de vie). N'empêche jamais le téléchargement.
      if (counts.blockers === 0) {
        api
          .post(`/api/tenders/${tender.id}/export`)
          .then(() => onChange())
          .catch(() => {});
      }
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Erreur téléchargement');
    } finally {
      setExporting(false);
    }
  }

  async function retryFullPipeline() {
    const ok = await confirm({
      title: 'Retraiter cet AO ?',
      message:
        'Le pipeline complet sera relance depuis le PDF. Les lignes, corrections, validation et Excel genere actuels seront remplaces.',
      confirmLabel: 'Retraiter',
      variant: 'danger',
    });
    if (!ok) return;
    setRetrying(true);
    setExportError(null);
    try {
      await api.post(`/api/tenders/${tender.id}/retry`);
      await onChange();
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Erreur retraitement');
    } finally {
      setRetrying(false);
    }
  }

  const counts = {
    haute: tender.lines.filter((l) => l.niveauConfiance === 'haute').length,
    moyenne: tender.lines.filter((l) => l.niveauConfiance === 'moyenne').length,
    basse: tender.lines.filter((l) => l.niveauConfiance === 'basse').length,
    pending: tender.lines.filter((l) => l.statutRevue === 'pending').length,
    blockers: tender.lines.filter(
      (l) => l.niveauConfiance === 'basse' && l.statutRevue === 'pending',
    ).length,
  };

  // ---------- Keyboard shortcuts ----------
  async function quickAction(act: 'accept' | 'reject') {
    const line = filtered[focusedLineIdx];
    if (!line || line.statutRevue !== 'pending') return;
    try {
      await api.patch(`/api/tenders/${tender.id}/lines/${line.id}`, { action: act });
      await onChange();
    } catch (err) {
      console.error(err);
    }
  }

  const shortcuts: Shortcut[] = [
    {
      key: 'j',
      label: 'Ligne suivante',
      handler: () => setFocusedLineIdx((i) => Math.min(i + 1, filtered.length - 1)),
    },
    {
      key: 'k',
      label: 'Ligne précédente',
      handler: () => setFocusedLineIdx((i) => Math.max(i - 1, 0)),
    },
    { key: 'a', label: 'Accepter la ligne focus', handler: () => quickAction('accept') },
    { key: 'r', label: 'Rejeter la ligne focus', handler: () => quickAction('reject') },
    { key: 'p', label: 'Toggle PDF split view', handler: () => setShowPdf((s) => !s) },
    {
      key: '?',
      label: 'Afficher cette aide',
      handler: () => setShowShortcutsHelp(true),
      withShift: true,
    },
  ];
  useKeyboardShortcuts(shortcuts);

  // Scroll la ligne focus en vue
  useEffect(() => {
    const line = filtered[focusedLineIdx];
    if (!line) return;
    document.getElementById(`line-${line.id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [focusedLineIdx, filtered]);

  return (
    <div>
      {dialog}

      {/* ARG Sprint 9 (B-65) — Banner constraints check (multi-step consistency) */}
      {constraints && constraints.summary.violated > 0 && (
        <div className="mb-4 border border-amber-900/60 bg-amber-900/10 px-4 py-3 text-2xs font-mono text-amber-200">
          <div className="flex items-start gap-2">
            <span className="text-amber-400 text-base">⚠</span>
            <div className="flex-1">
              <div className="font-medium mb-1">
                {constraints.summary.violated} contrainte(s) AO violée(s)
                <span className="text-amber-400/70">
                  {' '}· {constraints.summary.respected} respectée(s) · {constraints.summary.unknown} en attente
                </span>
              </div>
              <details className="mt-1">
                <summary className="cursor-pointer text-amber-300/80 hover:text-amber-200">
                  voir le détail
                </summary>
                <div className="mt-2 space-y-1.5">
                  {constraints.constraints.map((c) => {
                    const statusColor =
                      c.status === 'violated' ? 'text-red-400' :
                      c.status === 'respected' ? 'text-emerald-400' : 'text-steel-500';
                    const statusIcon =
                      c.status === 'violated' ? '✗' :
                      c.status === 'respected' ? '✓' : '?';
                    return (
                      <div key={c.id} className="border-l-2 border-amber-900/40 pl-2">
                        <div>
                          <span className={statusColor}>{statusIcon}</span>{' '}
                          <span className="text-steel-300">{c.constraint_type}</span>
                          <span className="text-steel-700">
                            {' · '}
                            {c.value_json}
                          </span>
                        </div>
                        {c.violation_detail && (
                          <div className="text-amber-300/70 ml-3 mt-0.5">
                            {c.violation_detail}
                          </div>
                        )}
                        {c.raw_text && (
                          <div className="text-steel-700 italic ml-3 mt-0.5">
                            extrait : "{c.raw_text.slice(0, 150)}{c.raw_text.length > 150 ? '…' : ''}"
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* HEADER : titre + actions principales (CTA primaire à droite) */}
      <div className="flex items-start justify-between mb-2 gap-4">
        <div className="min-w-0 flex-1">
          <div className="tc-label mb-2">tender · {tender.statut}</div>
          <h1 className="text-2xl font-display tracking-tight">{tender.nom}</h1>
          <p className="text-sm text-steel-500 mt-1">
            {tender.clientFinal && <>Client : <span className="text-steel-300">{tender.clientFinal}</span> · </>}
            <span className="text-steel-300">{tender.nbLignes}</span> lignes
            {tender.totalHt != null && tender.totalHt > 0 && (
              <> · Total estimé <span className="text-steel-300">{tender.totalHt.toLocaleString('fr-FR')} €</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <PresenceBadge tenderId={tender.id} />
          <button
            onClick={retryFullPipeline}
            disabled={retrying}
            className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Relancer le pipeline complet depuis le PDF"
          >
            <RotateCw aria-hidden className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
            <span>{retrying ? 'Relance...' : 'Retraiter'}</span>
          </button>
          <button
            onClick={() => setShowFinalize(true)}
            className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5"
            title="Soumettre la version finale corrigée pour apprentissage"
          >
            <Check aria-hidden className="w-4 h-4 text-emerald-400" strokeWidth={1.75} />
            <span>Finaliser</span>
          </button>
          <button
            onClick={downloadExcel}
            disabled={exporting || !tender.downloadPath}
            title={
              !tender.downloadPath
                ? 'Excel en cours de génération…'
                : counts.blockers > 0
                  ? 'Télécharger le brouillon (lignes sans produit surlignées en jaune)'
                  : 'Télécharger la proposition Excel'
            }
            className="tc-btn-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <Download aria-hidden className="w-4 h-4" strokeWidth={1.75} />
            <span>{exporting ? 'Téléchargement…' : 'Télécharger Excel'}</span>
          </button>
          <button
            onClick={() =>
              downloadFile(`/api/tenders/${tender.id}/download?kind=csv`).catch((e) =>
                setExportError(e instanceof Error ? e.message : 'Erreur download CSV'),
              )
            }
            title="Télécharger les lignes en CSV (analyse tableur)"
            className="tc-btn-secondary inline-flex items-center gap-1.5"
          >
            <Download aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>CSV</span>
          </button>
        </div>
      </div>

      {/* SOUS-HEADER : actions secondaires (vue / outils) */}
      <div className="flex items-center justify-between gap-3 mb-6 pb-3 border-b border-ink-700">
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setShowPdf(!showPdf)}
            className={`tc-btn-ghost text-2xs inline-flex items-center gap-1.5 ${
              showPdf ? 'text-brand-400' : ''
            }`}
            title="Afficher le PDF en split view"
          >
            <FileText aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>{showPdf ? 'Masquer PDF' : 'Voir PDF'}</span>
          </button>
          <button
            onClick={() => setShowAgentChat(!showAgentChat)}
            className={`tc-btn-ghost text-2xs inline-flex items-center gap-1.5 ${
              showAgentChat ? 'text-emerald-400' : ''
            }`}
            title="Discuter avec l'agent IA sur ce tender"
          >
            <Bot aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Agent</span>
          </button>
          <button
            onClick={() => setShowRunnerArtifacts(!showRunnerArtifacts)}
            className={`tc-btn-ghost text-2xs inline-flex items-center gap-1.5 ${
              showRunnerArtifacts ? 'text-amber-400' : ''
            }`}
            title="Afficher les artefacts techniques du runner"
          >
            <Cog aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Artefacts</span>
          </button>
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className={`tc-btn-ghost text-2xs inline-flex items-center gap-1.5 ${
              showTimeline ? 'text-brand-400' : ''
            }`}
            title="Timeline des events du tender"
          >
            <Clock aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Timeline</span>
          </button>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <ShareButton tenderId={tender.id} />
          <button
            type="button"
            onClick={() =>
              downloadFile(`/api/tenders/${tender.id}/download?kind=pdf`).catch((e) =>
                setExportError(e instanceof Error ? e.message : 'Erreur download'),
              )
            }
            className="tc-btn-ghost text-2xs inline-flex items-center gap-1.5"
            title="Télécharger le PDF original de l'AO"
          >
            <Download aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>PDF</span>
          </button>
          <Link
            to={`/tenders/${tender.id}/audit`}
            className="tc-btn-ghost text-2xs inline-flex items-center gap-1.5"
            title="Voir le journal d'audit complet"
          >
            <ScrollText aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Audit</span>
          </Link>
          <Link
            to={`/tenders/${tender.id}/print`}
            target="_blank"
            className="tc-btn-ghost text-2xs inline-flex items-center gap-1.5"
            title="Imprimer"
            aria-label="Imprimer"
          >
            <Printer aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Imprimer</span>
          </Link>
        </div>
      </div>

      {exportError && (
        <div className="bg-red-950/30 text-red-300 px-4 py-2 rounded mb-3 text-sm">{exportError}</div>
      )}

      {(() => {
        const g = stageGuidance(tender.statut);
        if (!g) return null;
        return (
          <div className="mb-4 border-l-2 border-brand-500 bg-brand-500/5 px-4 py-3">
            <div className="tc-label mb-1 text-brand-400">{g.step}</div>
            <div className="text-sm text-steel-200">{g.detail}</div>
          </div>
        );
      })()}

      {tender.linesStale && (
        <div className="mb-4 border-l-2 border-yellow-500 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-800 dark:text-yellow-200">
          ⚠ Des corrections ont été saisies mais <strong>pas encore recalculées</strong> — le tableau et les
          exports reflètent l'état d'avant. Clique « Recalculer avec mes corrections » pour les appliquer.
        </div>
      )}

      {(showRunnerArtifacts || tender.statut.startsWith('awaiting_')) && (
        <RunnerArtifactsPanel
          tenderId={tender.id}
          status={tender.statut}
          onChange={onChange}
          onClose={() => setShowRunnerArtifacts(false)}
        />
      )}

      <AttemptsHistoryPanel tenderId={tender.id} />

      {showTimeline && (
        <div className="tc-panel p-4 mb-4">
          <div className="tc-label mb-3">tender timeline</div>
          <TenderTimeline tenderId={tender.id} />
        </div>
      )}

      {showAgentChat && (
        <div className="tc-panel p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="tc-label">
              agent · this tender
              {tenderConvId && (
                <span className="ml-2 text-2xs text-steel-500 normal-case tracking-normal">
                  (conversation reprise)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {tenderConvId && (
                <button
                  type="button"
                  onClick={() => setTenderConvId(null)}
                  className="text-2xs font-mono uppercase tracking-widest text-steel-500 hover:text-amber-500"
                  title="Démarrer une nouvelle conversation (l'ancienne reste sauvegardée)"
                >
                  + nouvelle
                </button>
              )}
              <button
                onClick={() => setShowAgentChat(false)}
                className="text-steel-500 hover:text-steel-100 text-lg leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          {tenderConvId === undefined ? (
            <div className="text-sm text-steel-500 font-mono py-8 text-center">
              loading conversation…
            </div>
          ) : (
            <AgentChat
              key={tenderConvId ?? 'new'}
              contextKind="tender"
              tenderId={tender.id}
              initialConversationId={tenderConvId ?? undefined}
              height="500px"
              initialDraft={pendingMsg ?? undefined}
              onConversationCreated={(id) => {
                setTenderConvId(id);
                consumePendingMsg();
              }}
            />
          )}
        </div>
      )}

      {showFinalize && (
        <FinalizeTenderModal
          tenderId={tender.id}
          lines={tender.lines}
          onClose={() => setShowFinalize(false)}
          onFinalized={() => {
            setShowFinalize(false);
            void onChange();
          }}
        />
      )}

      {/* Résumé qualité (F) : lecture rapide de la confiance du devis. */}
      {tender.lines.length > 0 && (() => {
        const total = tender.lines.length;
        const resolved = tender.lines.filter((l) => (l.skuFinal ?? l.skuPropose) && l.niveauConfiance === 'haute').length;
        const toTreat = tender.lines.filter((l) => !(l.skuFinal ?? l.skuPropose) || l.niveauConfiance === 'basse' || l.contraintes === 'needs_attention').length;
        const floored = tender.lines.filter((l) => {
          const note = String((l.specsExtraites as Record<string, unknown> | null)?.commercial_note ?? '').toLowerCase();
          return note.includes('minimum') || note.includes('relev');
        }).length;
        const pct = total ? Math.round((resolved / total) * 100) : 0;
        return (
          <div className="tc-panel p-3 mb-3 flex items-center gap-4 flex-wrap text-sm">
            <span className="text-steel-300"><strong className="text-steel-100">{total}</strong> ligne{total > 1 ? 's' : ''}</span>
            <span className="text-emerald-700 dark:text-emerald-400">✓ {resolved} auto-résolue{resolved > 1 ? 's' : ''} ({pct}%)</span>
            <span className="text-yellow-700 dark:text-yellow-400">⚠ {toTreat} à traiter</span>
            {floored > 0 && <span className="text-orange-700 dark:text-orange-300">↑ {floored} relevée{floored > 1 ? 's' : ''} au plancher</span>}
          </div>
        );
      })()}

      {/* KPIs + filtres */}
      <div className="tc-panel p-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-2xs font-mono uppercase tracking-widest text-steel-500 mr-2">
            confiance
          </span>
          <FilterChip
            label="Haute"
            count={counts.haute}
            active={confFilter === 'haute'}
            onClick={() => setConfFilter(confFilter === 'haute' ? 'all' : 'haute')}
            color="green"
          />
          <FilterChip
            label="Moyenne"
            count={counts.moyenne}
            active={confFilter === 'moyenne'}
            onClick={() => setConfFilter(confFilter === 'moyenne' ? 'all' : 'moyenne')}
            color="orange"
          />
          <FilterChip
            label="Basse"
            count={counts.basse}
            active={confFilter === 'basse'}
            onClick={() => setConfFilter(confFilter === 'basse' ? 'all' : 'basse')}
            color="red"
          />
          <span className="w-px h-5 bg-ink-700 mx-2" aria-hidden />
          <span className="text-2xs font-mono uppercase tracking-widest text-steel-500 mr-2">
            statut
          </span>
          <FilterChip
            label="À revoir"
            count={counts.pending}
            active={statusFilter === 'pending'}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
          />
          {(confFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setConfFilter('all');
                setStatusFilter('all');
              }}
              className="text-xs text-gray-500 hover:underline"
            >
              Reset filtres
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {(() => {
              const pendingHaute = tender.lines.filter(
                (l) => l.niveauConfiance === 'haute' && l.statutRevue === 'pending',
              ).length;
              if (pendingHaute === 0) return null;
              return (
                <button
                  onClick={bulkAcceptHaute}
                  disabled={bulking}
                  className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded text-sm disabled:opacity-50"
                >
                  {bulking ? (
                    'Acceptation…'
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <Check aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                      <span>Accepter toutes les {pendingHaute} hautes</span>
                    </span>
                  )}
                </button>
              );
            })()}
            {/* ARG B-07 — bouton "tout vérifier" : visible dès qu'au moins une
                ligne est non-CONFIRMED. Cohabite avec "Accepter hautes" :
                les 2 actions sont orthogonales (statutRevue legacy vs
                infoOutcomeStatus ARG). */}
            {(() => {
              const pendingArg = tender.lines.filter(
                (l) => l.infoOutcomeStatus !== 'INFO_CONFIRMED_SUCCESS',
              ).length;
              if (pendingArg === 0) return null;
              return (
                <button
                  onClick={bulkConfirmArg}
                  disabled={bulking}
                  className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded text-sm disabled:opacity-50"
                >
                  {bulking ? (
                    'Vérification…'
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <ShieldCheck aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                      <span>Tout vérifier ({pendingArg})</span>
                    </span>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Tableau par lot — wrappé dans un grid si split-view PDF active.
          ARG Sprint 9 (B-67) — Le viewer est extracted dans `PdfSourceViewer`
          avec header de traçabilité (page + designation hint + close button). */}
      <div className={showPdf ? 'grid grid-cols-2 gap-4' : ''}>
        {showPdf && (
          <PdfSourceViewer
            tenderId={tender.id}
            page={pdfPage}
            designation={pdfSelectedHint?.designation ?? null}
            bbox={pdfSelectedHint?.bbox ?? null}
            onClose={() => {
              setShowPdf(false);
              setPdfSelectedHint(null);
            }}
          />
        )}

        <div>
          {Object.keys(lots).length === 0 ? (
            <p className="tc-panel p-6 text-center text-steel-400 text-sm">
              {tender.statut === 'awaiting_params_review'
                ? 'Les lignes du devis seront générées après validation des paramètres — réponds aux questions générales ci-dessus, puis « Recalculer ».'
                : tender.lines.length === 0
                  ? 'Aucune ligne pour le moment.'
                  : 'Aucune ligne ne correspond aux filtres.'}
            </p>
          ) : (
            Object.entries(lots).map(([lotName, lines]) => (
              <section key={lotName} className="tc-panel mb-4 overflow-hidden">
                <h2 className="px-4 py-2 bg-ink-800/50 text-2xs font-mono uppercase tracking-widest text-steel-400 border-b border-ink-700 inline-flex items-center gap-2">
                  <span>{lotName}</span>
                  <span className="text-steel-700">·</span>
                  <span className="text-steel-500">{lines.length} ligne{lines.length > 1 ? 's' : ''}</span>
                </h2>
                <table className="w-full text-sm table-fixed">
                  <thead className="text-left text-2xs font-mono uppercase tracking-widest text-steel-500 border-b border-ink-700 bg-ink-900/50">
                    <tr>
                      <th className="px-2 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={lines.every((l) => selectedLineIds.has(l.id))}
                          onChange={(e) => {
                            setSelectedLineIds((prev) => {
                              const next = new Set(prev);
                              for (const l of lines) {
                                if (e.target.checked) next.add(l.id);
                                else next.delete(l.id);
                              }
                              return next;
                            });
                          }}
                          aria-label={`Sélectionner toutes les lignes du ${lotName}`}
                        />
                      </th>
                      <th className="px-4 py-2 w-12 hidden sm:table-cell">Ligne</th>
                      <th className="px-4 py-2">Désignation AO</th>
                      <th className="px-4 py-2 w-28 md:w-40">SKU proposé</th>
                      <th className="px-4 py-2 w-20 hidden lg:table-cell">Qté</th>
                      <th className="px-4 py-2 w-28 text-right hidden md:table-cell">Prix U. (HT)</th>
                      <th className="px-4 py-2 w-28 hidden lg:table-cell">Confiance</th>
                      <th className="px-4 py-2 w-24 hidden sm:table-cell">Statut</th>
                      <th className="px-4 py-2 w-28 lg:w-36">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <LineRow
                        key={l.id}
                        line={l}
                        tenderId={tender.id}
                        onChange={onChange}
                        checkpoint={tender.statut === 'awaiting_references_review' || tender.statut === 'awaiting_final_review'}
                        isFocused={filtered[focusedLineIdx]?.id === l.id}
                        commentCount={commentCounts[l.id] ?? 0}
                        selected={selectedLineIds.has(l.id)}
                        onToggleSelect={() =>
                          setSelectedLineIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(l.id)) next.delete(l.id);
                            else next.add(l.id);
                            return next;
                          })
                        }
                        onCommentChange={() =>
                          api
                            .get<{ counts: Record<string, number> }>(
                              `/api/tenders/${tender.id}/comments-counts`,
                            )
                            .then((d) => setCommentCounts(d.counts ?? {}))
                        }
                        onPagePdfClick={
                          // ARG Sprint 9 (B-67) — Le bouton "voir source" auto-ouvre
                          // le panneau PDF s'il est fermé (= UX un seul clic au lieu
                          // de deux). Source de la page : `sourceBbox.page` Sprint 9+
                          // sinon fallback sur `pagePdf` legacy (lignes pré-Sprint 9
                          // ou backfill incomplet).
                          ((): (() => void) | undefined => {
                            const targetPage =
                              l.sourceBbox?.page ?? l.pagePdf ?? null;
                            if (targetPage == null) return undefined;
                            return () => {
                              setPdfPage(targetPage);
                              setPdfSelectedHint({
                                designation: l.designationAo,
                                bbox: l.sourceBbox
                                  ? {
                                      x: l.sourceBbox.x,
                                      y: l.sourceBbox.y,
                                      w: l.sourceBbox.w,
                                      h: l.sourceBbox.h,
                                    }
                                  : null,
                              });
                              if (!showPdf) setShowPdf(true);
                            };
                          })()
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </section>
            ))
          )}
        </div>
      </div>

      <BulkLineActions
        tenderId={tender.id}
        selectedLineIds={Array.from(selectedLineIds)}
        selectedLines={tender.lines.filter((l) => selectedLineIds.has(l.id))}
        checkpoint={tender.statut === 'awaiting_references_review' || tender.statut === 'awaiting_final_review'}
        onDone={() => {
          void onChange();
        }}
        onClear={() => setSelectedLineIds(new Set())}
      />

      {showShortcutsHelp && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowShortcutsHelp(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-ink-900 border border-ink-700 rounded shadow-lg max-w-md w-full p-5 text-steel-100"
          >
            <h3 className="font-semibold text-lg mb-3">⌨ Raccourcis clavier</h3>
            <ul className="text-sm space-y-2">
              {shortcuts.map((s) => (
                <li key={s.key} className="flex items-center gap-3">
                  <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border rounded font-mono text-xs min-w-[2rem] text-center">
                    {s.withShift ? `⇧ ${s.key}` : s.key}
                  </kbd>
                  <span>{s.label}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-4">
              Désactivé quand un champ de saisie a le focus.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowShortcutsHelp(false)}
                className="bg-primary-500 text-white px-4 py-1.5 rounded text-sm"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Line row
// ============================================================

type RuleSuggestion = {
  kind: 'sku_substitution';
  skuInitial: string;
  skuCorrige: string;
  nbOccurrences: number;
};

function LineRow({
  line,
  tenderId,
  onChange,
  commentCount,
  onCommentChange,
  onPagePdfClick,
  isFocused,
  selected,
  onToggleSelect,
  checkpoint,
}: {
  line: TenderLine;
  tenderId: string;
  onChange: () => Promise<void>;
  commentCount: number;
  onCommentChange: () => Promise<unknown>;
  onPagePdfClick?: () => void;
  isFocused?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** true pendant un checkpoint runner (awaiting_*) : active la correction inline
   *  routée vers /answers (prise au « Recalculer »), pour les lignes en attention. */
  checkpoint?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  // Désignation repliée par défaut (2 lignes) → liste compacte, actions toujours
  // visibles ; « voir plus » / clic déplie specs + justification + rationale.
  const [expanded, setExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [askingClient, setAskingClient] = useState(false);
  const [questionText, setQuestionText] = useState(line.questionClient ?? '');
  const [skuFinal, setSkuFinal] = useState(line.skuFinal ?? line.skuPropose ?? '');
  const [comment, setComment] = useState(line.commentaireRevue ?? '');
  const [busy, setBusy] = useState<null | 'accept' | 'reject' | 'correct' | 'wait_client'>(null);
  const [busyConfirmArg, setBusyConfirmArg] = useState(false);
  const [ruleSuggestion, setRuleSuggestion] = useState<RuleSuggestion | null>(null);
  // ARG Sprint 9 (B-61) — Mode ASK_ADMIN dialog state
  const [clarifyDialogOpen, setClarifyDialogOpen] = useState<string | null>(null);
  const [clarifySkuChoice, setClarifySkuChoice] = useState('');
  const [clarifyFreeText, setClarifyFreeText] = useState('');
  const [clarifyReason, setClarifyReason] = useState('');
  const [clarifyBusy, setClarifyBusy] = useState(false);
  const [productSkuOpen, setProductSkuOpen] = useState<string | null>(null);

  // --- Correction inline « checkpoint » (routée vers /answers, prise au Recalculer) ---
  const specs = (line.specsExtraites ?? null) as Record<string, unknown> | null;
  const runnerTable = (specs?.table ?? specs?.source_table ?? null) as string | null;
  const runnerItem = (specs?.item_id ?? null) as string | null;
  // En checkpoint, TOUTE ligne runner est corrigible via l'unique mécanisme
  // (SKU cell -> /answers -> recalcul). Le ✏ direct (PATCH) est masqué en
  // checkpoint : une seule façon de corriger une ligne à cette étape.
  const runnerFixable = Boolean(checkpoint) && Boolean(runnerTable);
  const [correcting, setCorrecting] = useState(false);
  const [cSku, setCSku] = useState(line.skuPropose ?? '');
  const [cQty, setCQty] = useState('');
  const [cNote, setCNote] = useState('');
  const [cPromote, setCPromote] = useState(false);
  const [cCreateSku, setCCreateSku] = useState(false);
  const [cDesignation, setCDesignation] = useState('');
  const [cBusy, setCBusy] = useState(false);
  const [cMsg, setCMsg] = useState<string | null>(null);

  async function saveRunnerCorrection() {
    if (!cSku.trim() && !cQty.trim() && !cNote.trim()) {
      setCMsg('Renseigne un SKU, une quantité ou une note.');
      return;
    }
    setCBusy(true);
    setCMsg(null);
    try {
      const res = await api.post<{ remaining: number; ruleApplied?: boolean; ruleConflict?: string | null; skuUnknown?: boolean }>(
        `/api/tenders/${tenderId}/answers`,
        {
          source: 'references',
          path: null,
          code: 'reference_needs_attention',
          table: runnerTable,
          itemId: runnerItem,
          lotId: line.lot,
          message: line.justification ?? null,
          answer: cNote,
          ref: cSku,
          qty: cQty.trim() || null,
          promoteToRule: cPromote,
          ruleNote: cNote || null,
          createSku: cCreateSku,
          skuDesignation: cDesignation || null,
        },
      );
      const base = res.skuUnknown && !cCreateSku
        ? `⚠ SKU « ${cSku} » inconnu dans GS.DATA. Corrige-le, ou coche « créer ».`
        : res.ruleConflict
          ? `⚠ Conflit de règle : ${res.ruleConflict}`
          : cPromote
            ? (res.ruleApplied ? '✅ Correction + règle publiée dans GS.DATA.' : 'Correction enregistrée + règle proposée (revue admin).')
            : 'Correction enregistrée.';
      setCMsg(`${base} Clique « Recalculer avec mes corrections » (en haut) pour l'appliquer.`);
      await onChange();
    } catch (err) {
      setCMsg(err instanceof Error ? err.message : 'Erreur lors de la correction');
    } finally {
      setCBusy(false);
    }
  }

  // ARG B-07 — clic "vérifié" : POST /api/tenders/:id/lines/:lineId/confirm
  // Bascule INFO_UNCERTAIN_SUCCESS / LOW_CONFIDENCE → INFO_CONFIRMED_SUCCESS.
  // Anti-silence-bias : seul ce clic explicite peut faire passer en CONFIRMED.
  async function confirmArg() {
    setBusyConfirmArg(true);
    try {
      await api.post(`/api/tenders/${tenderId}/lines/${line.id}/confirm`);
      await onChange();
    } catch (err) {
      console.error('ARG confirm failed', err);
    } finally {
      setBusyConfirmArg(false);
    }
  }

  async function action(act: 'accept' | 'correct' | 'reject' | 'wait_client') {
    setBusy(act);
    try {
      const res = await api.patch<{ ruleSuggestion: RuleSuggestion | null }>(
        `/api/tenders/${tenderId}/lines/${line.id}`,
        {
          action: act,
          skuFinal: act === 'correct' ? skuFinal : undefined,
          commentaireRevue: comment || undefined,
          questionClient: act === 'wait_client' ? questionText : undefined,
        },
      );
      setEditing(false);
      setAskingClient(false);
      if (res?.ruleSuggestion) setRuleSuggestion(res.ruleSuggestion);
      await onChange();
    } catch (err) {
      console.error('Action failed', err);
    } finally {
      setBusy(null);
    }
  }

  const reviewed = line.statutRevue !== 'pending';
  const displaySku = line.skuFinal ?? line.skuPropose;
  // ARG Sprint 6 (B-44) — détection takeover via colonne dédiée
  // `tender_lines.arg_takeover` (migration 0040). Sprint 4 (B-30) utilisait
  // un prefix string sur `justification` — fragile (cassé si admin édite la
  // justif). Fallback sur le prefix string conservé pour rétrocompat avec
  // les lignes legacy non-backfillées (= safety net pendant la transition,
  // une fois le backfill propagé en prod cette branche peut être retirée).
  // Si l'admin a override le SKU (skuFinal != null && != skuPropose), le
  // badge n'a plus de sens : on ne l'affiche que si skuFinal vaut le sku
  // ARG OU si pas encore reviewé.
  const argTakeoverFlag =
    line.argTakeover === true
    || (typeof line.justification === 'string'
      && line.justification.startsWith('[ARG TAKEOVER'));
  const isArgTakeover =
    argTakeoverFlag
    && (line.skuFinal === null || line.skuFinal === line.skuPropose);
  // ARG Sprint 9 (B-61) — Mode ASK_ADMIN : si needs_clarification est true,
  // afficher un badge orange + bouton dialog au lieu du SKU normal.
  const isAskAdmin = line.needsClarification === true;

  return (
    <>
      <tr
        id={`line-${line.id}`}
        className={`border-b border-ink-700/60 last:border-b-0 align-top transition-colors group ${
          line.statutRevue === 'rejected' ? 'opacity-50' : ''
        } ${
          isFocused
            ? 'bg-brand-500/10 ring-1 ring-inset ring-brand-500/40'
            : selected
              ? 'bg-blue-500/10'
              : 'hover:bg-ink-800/50'
        }`}
      >
        <td className="px-2 py-2 text-center">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              aria-label={`Sélectionner la ligne ${line.numeroLigne ?? line.id}`}
            />
          )}
        </td>
        <td className="px-4 py-2 text-xs text-steel-500 font-mono whitespace-nowrap hidden sm:table-cell">
          <div className="text-steel-400">{line.numeroLigne ?? '—'}</div>
          {line.pagePdf && (
            onPagePdfClick ? (
              <button
                onClick={onPagePdfClick}
                className="block text-brand-400 hover:text-brand-300 hover:underline mt-0.5 text-2xs"
                title="Voir cette page dans le PDF"
              >
                p.{line.pagePdf}
              </button>
            ) : (
              <div className="text-steel-700 mt-0.5 text-2xs">p.{line.pagePdf}</div>
            )
          )}
        </td>
        <td className="px-4 py-2 align-top">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-left w-full"
            title={expanded ? 'Réduire la ligne' : 'Voir le détail de la ligne'}
          >
            <div className={`text-steel-100 break-words ${expanded ? '' : 'line-clamp-2'}`}>
              {line.designationAo}
            </div>
          </button>
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-1 text-2xs font-mono uppercase tracking-widest text-brand-400 hover:text-brand-300"
            >
              voir plus
            </button>
          )}
          {expanded && (
          <>
          {line.specsExtraites && (() => {
            const entries = Object.entries(line.specsExtraites).filter(
              ([, v]) => v != null && v !== '' && (!Array.isArray(v) || v.length > 0),
            );
            if (entries.length === 0) return null;
            // Format compact : "DN15 · PN16 · cuivre · 2 normes"
            const parts: string[] = [];
            for (const [k, v] of entries) {
              if (k === 'dn') parts.push(`DN${v}`);
              else if (k === 'pn') parts.push(`PN${v}`);
              else if (k === 'normes' && Array.isArray(v)) {
                if (v.length === 1) parts.push(String(v[0]));
                else if (v.length > 1) parts.push(`${v.length} normes`);
              } else if (Array.isArray(v)) parts.push(v.join(','));
              else parts.push(String(v));
            }
            return (
              <div className="mt-1 text-2xs font-mono text-steel-500 truncate" title={parts.join(' · ')}>
                {parts.slice(0, 4).join(' · ')}
                {parts.length > 4 && <span className="text-steel-700"> · +{parts.length - 4}</span>}
              </div>
            );
          })()}
          {line.justification && (() => {
            const raw = line.justification;
            const isNoMatch = /aucun produit|aucune correspondance/i.test(raw);
            const isTechError = /erreur interne|internal error/i.test(raw);
            // Messages condensés sans répéter le label
            const condensed = isTechError
              ? 'Erreur technique du matcher'
              : isNoMatch
                ? 'Aucun produit éligible (DN, PN, normes obligatoires)'
                : raw;
            const cls = isTechError
              ? 'border-l-2 border-red-500 bg-red-900/20 text-red-300'
              : isNoMatch
                ? 'border-l-2 border-amber-500 bg-amber-900/15 text-amber-300'
                : 'border-l-2 border-ink-700 text-steel-500';
            return (
              <div className={`mt-2 px-2 py-1 text-2xs ${cls} rounded-sm leading-snug`}>
                {condensed}
              </div>
            );
          })()}
          <ProposalRationale line={line} />
          {line.parametricDecision && (
            <ParametricBadge decision={line.parametricDecision} />
          )}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="mt-2 block text-2xs font-mono uppercase tracking-widest text-steel-500 hover:text-steel-300"
          >
            voir moins
          </button>
          </>
          )}
        </td>
        <td className="px-4 py-2 font-mono text-xs">
          <div className="flex items-center gap-1.5">
            {displaySku ? (
              <>
                <button
                  type="button"
                  onClick={() => setProductSkuOpen(displaySku)}
                  className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 hover:underline"
                  title="Afficher toutes les caracteristiques du produit"
                >
                  <span>{displaySku}</span>
                  <Info aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
                {runnerFixable && (
                  <button
                    type="button"
                    onClick={() => setCorrecting((v) => !v)}
                    title="Corriger le SKU / la quantité (pris au « Recalculer »)"
                    aria-label="Corriger pour le recalcul"
                    className="text-amber-400 hover:text-amber-300"
                  >
                    <Pencil aria-hidden className="w-3 h-3" strokeWidth={1.75} />
                  </button>
                )}
              </>
            ) : runnerFixable ? (
              <button
                type="button"
                onClick={() => setCorrecting((v) => !v)}
                title="Aucun SKU — cliquer pour corriger (pris au « Recalculer »)"
                className="inline-flex items-center gap-1 text-2xs font-mono uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/40 hover:bg-amber-500/20 px-1.5 py-0.5 rounded"
              >
                <span>aucun</span>
                <Pencil aria-hidden className="w-3 h-3" strokeWidth={1.75} />
              </button>
            ) : (
              <span className="inline-flex items-center text-2xs font-mono uppercase tracking-widest text-amber-400/80 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                aucun
              </span>
            )}
            {/* ARG Sprint 4 (B-30) — badge "🤖 ARG" pour les SKU validés par le takeover B-13.
                Sprint 6 (B-44) — détection via colonne `arg_takeover` (robuste vs prefix string). */}
            {isArgTakeover && (
              <Tooltip
                label="SKU validé par ARG (Adaptive Reasoning Graph). Override automatique du Matcher legacy v2 par le target_sku ARG (cf flag arg_takes_over_sku)."
                side="top"
              >
                <span className="inline-flex items-center gap-0.5 text-2xs font-mono uppercase tracking-widest text-purple-300 bg-purple-500/10 border border-purple-500/30 px-1.5 py-0.5 rounded">
                  <Bot aria-hidden className="w-3 h-3" strokeWidth={1.75} />
                  <span>arg</span>
                </span>
              </Tooltip>
            )}
            {/* ARG Sprint 9 (B-61) — badge "🤔 question" pour les lignes ASK_ADMIN.
                Pattern Kepler : "Claude stopped and asked the analyst to decide". */}
            {isAskAdmin && (
              <Tooltip
                label="Le matcher demande une clarification (mode verifiable, ASK_ADMIN). Cliquer pour ouvrir la question structurée."
                side="top"
              >
                <button
                  type="button"
                  onClick={() => setClarifyDialogOpen(line.id)}
                  className="inline-flex items-center gap-0.5 text-2xs font-mono uppercase tracking-widest text-orange-300 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 px-1.5 py-0.5 rounded cursor-pointer"
                >
                  <span>🤔 question</span>
                </button>
              </Tooltip>
            )}
            {/* ARG Sprint 9 (B-69) — badge "🦉 opus" pour les lignes escaladées.
                Pattern Kepler : "we paid for Opus quality only on the cases that needed it". */}
            {line.escalationUsed === true && (
              <Tooltip
                label="Cette ligne a été escaladée à Claude Opus 4.5 car Sonnet 4.5 hésitait. Opus a produit un meilleur résultat. Cost ~5× supérieur à Sonnet seul."
                side="top"
              >
                <span className="inline-flex items-center gap-0.5 text-2xs font-mono uppercase tracking-widest text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/30 px-1.5 py-0.5 rounded">
                  <span>🦉 opus</span>
                </span>
              </Tooltip>
            )}
          </div>
        </td>
        <td className="px-4 py-2 whitespace-nowrap hidden lg:table-cell">
          <span className="font-mono text-steel-100">{line.quantite ?? '—'}</span>{' '}
          <span className="text-2xs text-steel-500 uppercase">{line.unite}</span>
        </td>
        <td className="px-4 py-2 whitespace-nowrap text-right hidden md:table-cell">
          {line.prixUnitaireApplique != null ? (
            <div>
              <div className="font-mono text-sm text-steel-100">
                {line.prixUnitaireApplique.toLocaleString('fr-FR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                €
              </div>
              {line.totalLigneHt != null && (
                <div className="text-2xs text-steel-500 font-mono">
                  ={' '}
                  {line.totalLigneHt.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                </div>
              )}
            </div>
          ) : (
            <span className="text-steel-700">—</span>
          )}
        </td>
        <td className="px-4 py-2 whitespace-nowrap hidden lg:table-cell">
          <div className="flex items-center gap-2">
            {line.niveauConfiance && <ConfidenceBadge level={line.niveauConfiance} score={line.scoreConfiance} />}
            {/* ARG B-07 : pastille C_info + badge InfoOutcomeStatus si la ligne a tourné en mode ARG */}
            {line.cInfo != null && <ArgConfidenceDot cInfo={line.cInfo} />}
          </div>
          {line.infoOutcomeStatus && (
            <div className="mt-1">
              <InfoOutcomeBadge status={line.infoOutcomeStatus} />
            </div>
          )}
        </td>
        <td className="px-4 py-2 whitespace-nowrap hidden sm:table-cell">
          <ReviewBadge status={line.statutRevue} />
        </td>
        <td className="px-4 py-2">
          <div className="flex gap-1 items-center">
            {!reviewed && (
              <>
                <ActionBtn icon={Check} title="Accepter" busy={busy === 'accept'} onClick={() => action('accept')} color="green" />
                {/* En checkpoint, la correction passe par le ✏ de la colonne SKU
                    (routée /answers + recalcul) : on masque ici le ✏ direct (PATCH)
                    pour éviter DEUX mécanismes de correction sur la même ligne. */}
                {!checkpoint && (
                  <ActionBtn icon={Pencil} title="Corriger" busy={false} onClick={() => setEditing(!editing)} color="blue" />
                )}
                <ActionBtn icon={X} title="Rejeter" busy={busy === 'reject'} onClick={() => action('reject')} color="red" />
                <ActionBtn
                  icon={Pause}
                  title="En attente clarification client"
                  busy={false}
                  onClick={() => setAskingClient(true)}
                  color="purple"
                />
              </>
            )}
            {reviewed && (
              <button
                onClick={() => setEditing(!editing)}
                title="Modifier"
                aria-label="Modifier"
                className="text-gray-500 hover:text-amber-500 mr-1 inline-flex items-center w-7 h-7 justify-center rounded"
              >
                <Pencil aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            )}
            <button
              onClick={() => setShowComments(!showComments)}
              className={`text-xs px-1.5 py-0.5 rounded ${
                commentCount > 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-700'
              }`}
              title="Commentaires"
            >
              <span className="inline-flex items-center gap-1">
                <MessageSquare aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                {commentCount > 0 ? commentCount : ''}
              </span>
            </button>
            {/* ARG B-07 : bouton "vérifié" — bascule en INFO_CONFIRMED_SUCCESS.
                Disponible même si line.cInfo est null (D5) — confirme la ligne
                manuellement, c'est l'unique chemin vers CONFIRMED_SUCCESS
                (anti-silence-bias). Désactivé si déjà CONFIRMED. */}
            {line.infoOutcomeStatus !== 'INFO_CONFIRMED_SUCCESS' && (
              <button
                onClick={confirmArg}
                disabled={busyConfirmArg}
                title={
                  line.cInfo != null
                    ? `Marquer comme vérifié (c_info=${line.cInfo.toFixed(2)})`
                    : 'Marquer comme vérifié'
                }
                aria-label="Marquer comme vérifié"
                className="text-emerald-500 hover:text-emerald-400 disabled:opacity-40 inline-flex items-center w-7 h-7 justify-center rounded"
              >
                <ShieldCheck aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {showComments && (
        <tr>
          <td colSpan={9} className="bg-ink-900/60 px-4 py-3">
            <CommentThread
              tenderId={tenderId}
              lineId={line.id}
              onChange={onCommentChange}
            />
          </td>
        </tr>
      )}
      {askingClient && (
        <tr className="bg-purple-500/10">
          <td colSpan={9} className="px-4 py-3">
            <label className="block text-xs font-medium mb-1">
              Question à poser au client (la ligne sera marquée "en attente"
              et exclue de l'export tant qu'elle n'est pas résolue)
            </label>
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="ex: Préciser si raccordement taraudé ou à brides ?"
              rows={2}
              autoFocus
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => action('wait_client')}
                disabled={busy === 'wait_client' || !questionText.trim()}
                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
              >
                {busy === 'wait_client' ? 'Enregistrement…' : 'Mettre en attente client'}
              </button>
              <button
                onClick={() => setAskingClient(false)}
                className="text-sm text-gray-500 hover:underline"
              >
                Annuler
              </button>
            </div>
          </td>
        </tr>
      )}
      {line.statutRevue === 'waiting_client' && line.questionClient && !askingClient && (
        <tr className="bg-purple-500/10">
          <td colSpan={9} className="px-4 py-2 text-xs">
            <span className="font-medium text-purple-800">⏸ Question client :</span>{' '}
            <span className="italic">{line.questionClient}</span>
          </td>
        </tr>
      )}
      {ruleSuggestion && (
        <SuggestionModal
          suggestion={ruleSuggestion}
          onClose={() => setRuleSuggestion(null)}
        />
      )}
      {productSkuOpen && (
        <ProductDetailModal
          sku={productSkuOpen}
          onClose={() => setProductSkuOpen(null)}
        />
      )}
      {editing && (
        <tr className="bg-blue-500/10">
          <td colSpan={9} className="px-4 py-3">
            <div className="grid grid-cols-2 gap-3 max-w-2xl">
              <div>
                <label className="block text-xs font-medium mb-1">SKU final</label>
                <SkuAutocomplete
                  value={skuFinal}
                  onChange={setSkuFinal}
                  placeholder="Taper SKU ou désignation…"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Commentaire</label>
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  placeholder="Raison de la correction…"
                />
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => action('correct')}
                disabled={busy === 'correct' || !skuFinal}
                className="bg-primary-500 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                {busy === 'correct' ? 'Enregistrement…' : 'Enregistrer la correction'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-sm text-gray-500 hover:underline"
              >
                Annuler
              </button>
            </div>
          </td>
        </tr>
      )}

      {/* Correction inline « checkpoint » — routée vers /answers (prise au Recalculer). */}
      {correcting && (
        <tr className="bg-amber-950/20">
          <td colSpan={9} className="px-4 py-3 border-l-2 border-amber-500">
            <div className="text-2xs font-mono uppercase tracking-widest text-amber-300 mb-2">
              Corriger pour le recalcul — {line.designationAo}
            </div>
            {line.justification && (
              <div className="text-xs text-steel-400 mb-2">{line.justification}</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3 max-w-3xl">
              <div>
                <label className="block text-2xs font-mono uppercase tracking-widest text-steel-400 mb-1">SKU catalogue</label>
                <SkuAutocomplete value={cSku} onChange={setCSku} placeholder="Taper SKU ou désignation…" />
              </div>
              <div>
                <label className="block text-2xs font-mono uppercase tracking-widest text-steel-400 mb-1">Qté</label>
                <input
                  value={cQty}
                  onChange={(e) => setCQty(e.target.value)}
                  inputMode="decimal"
                  className="w-full bg-ink-900 border border-ink-700 px-2 py-1.5 text-sm text-steel-100 font-mono text-right"
                  placeholder={line.quantite != null ? String(line.quantite) : '—'}
                />
              </div>
            </div>
            <label className="block mt-2 max-w-3xl">
              <span className="block text-2xs font-mono uppercase tracking-widest text-steel-400 mb-1">note / justification (option.)</span>
              <input
                value={cNote}
                onChange={(e) => setCNote(e.target.value)}
                className="w-full bg-ink-900 border border-ink-700 px-2 py-1.5 text-sm text-steel-100"
                placeholder="Précision, choix technique…"
              />
            </label>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-1.5 text-2xs text-amber-300 cursor-pointer">
                <input type="checkbox" checked={cPromote} onChange={(e) => setCPromote(e.target.checked)} />
                ⭐ Proposer comme règle générale (GS.DATA)
              </label>
              {cPromote && (
                <label className="inline-flex items-center gap-1.5 text-2xs text-steel-400 cursor-pointer">
                  <input type="checkbox" checked={cCreateSku} onChange={(e) => setCCreateSku(e.target.checked)} />
                  SKU absent → le créer
                </label>
              )}
              {cPromote && cCreateSku && (
                <input
                  value={cDesignation}
                  onChange={(e) => setCDesignation(e.target.value)}
                  className="bg-ink-900 border border-ink-700 px-2 py-1 text-2xs text-steel-100 flex-1 min-w-[200px]"
                  placeholder="Désignation du nouveau produit"
                />
              )}
            </div>
            {cMsg && <div className="mt-2 text-xs text-amber-200">{cMsg}</div>}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={saveRunnerCorrection}
                disabled={cBusy}
                className="bg-amber-500 hover:bg-amber-400 text-ink-950 px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                {cBusy ? 'Enregistrement…' : 'Enregistrer la correction'}
              </button>
              <button type="button" onClick={() => setCorrecting(false)} className="text-sm text-steel-500 hover:underline">
                Fermer
              </button>
            </div>
          </td>
        </tr>
      )}

      {/* ARG Sprint 9 (B-61) — ASK_ADMIN clarification dialog */}
      {clarifyDialogOpen === line.id && (
        <tr>
          <td colSpan={11} className="bg-orange-900/10 border-l-4 border-orange-500 p-0">
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-2xs font-mono uppercase tracking-widest text-orange-400 mb-1">
                    🤔 clarification requise (mode ASK_ADMIN)
                  </div>
                  <div className="text-sm text-steel-100 font-mono whitespace-pre-wrap">
                    {line.questionClient ?? 'Question non disponible'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setClarifyDialogOpen(null);
                    setClarifySkuChoice('');
                    setClarifyFreeText('');
                    setClarifyReason('');
                  }}
                  className="text-2xs text-steel-500 hover:text-steel-100 font-mono"
                >
                  ✕ fermer
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Option 1 : sélectionner un SKU */}
                <div className="border border-ink-700 p-3">
                  <div className="text-2xs font-mono uppercase tracking-widest text-steel-300 mb-2">
                    option A — choisir un SKU
                  </div>
                  <input
                    type="text"
                    value={clarifySkuChoice}
                    onChange={(e) => setClarifySkuChoice(e.target.value)}
                    placeholder="SKU précis (ex: V001)"
                    className="bg-ink-900 border border-ink-700 px-3 py-2 text-sm text-steel-100 font-mono w-full mb-2"
                  />
                  <input
                    type="text"
                    value={clarifyReason}
                    onChange={(e) => setClarifyReason(e.target.value)}
                    placeholder="Raison du choix (= justifie)"
                    className="bg-ink-900 border border-ink-700 px-3 py-2 text-2xs text-steel-100 font-mono w-full"
                  />
                </div>

                {/* Option 2 : free text */}
                <div className="border border-ink-700 p-3">
                  <div className="text-2xs font-mono uppercase tracking-widest text-steel-300 mb-2">
                    option B — texte libre
                  </div>
                  <textarea
                    value={clarifyFreeText}
                    onChange={(e) => setClarifyFreeText(e.target.value)}
                    placeholder="Précisions / questions / contexte (= matcher re-tournera avec ce contexte)"
                    rows={3}
                    className="bg-ink-900 border border-ink-700 px-3 py-2 text-2xs text-steel-100 font-mono w-full resize-none"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setClarifyBusy(true);
                    try {
                      const body: { sku_chosen?: string; free_text?: string; reason?: string } = {};
                      if (clarifySkuChoice.trim()) {
                        body.sku_chosen = clarifySkuChoice.trim();
                        if (clarifyReason.trim()) body.reason = clarifyReason.trim();
                      } else if (clarifyFreeText.trim()) {
                        body.free_text = clarifyFreeText.trim();
                      } else {
                        return; // pas de choix valide
                      }
                      await api.post(
                        `/api/tenders/${tenderId}/lines/${line.id}/clarify`,
                        body,
                      );
                      setClarifyDialogOpen(null);
                      setClarifySkuChoice('');
                      setClarifyFreeText('');
                      setClarifyReason('');
                      await onChange();
                    } catch (err) {
                      console.error('clarify failed', err);
                    } finally {
                      setClarifyBusy(false);
                    }
                  }}
                  disabled={
                    clarifyBusy
                    || (!clarifySkuChoice.trim() && !clarifyFreeText.trim())
                  }
                  className="bg-orange-500 hover:bg-orange-400 text-ink-950 px-4 py-1.5 rounded text-sm disabled:opacity-50"
                >
                  {clarifyBusy ? 'Envoi…' : 'Envoyer la clarification'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================
// Suggestion modal
// ============================================================

function SuggestionModal({
  suggestion,
  onClose,
}: {
  suggestion: RuleSuggestion;
  onClose: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function createRule() {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/rules', {
        type: 'equivalence',
        scope: 'global',
        priority: 100,
        payload: {
          sku_initial: suggestion.skuInitial,
          sku_corrige: suggestion.skuCorrige,
        },
        description: `Auto-suggéré après ${suggestion.nbOccurrences} corrections similaires : ${suggestion.skuInitial} —  ${suggestion.skuCorrige}`,
        actif: true,
      });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <tr>
      <td colSpan={9} className="p-0">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="suggestion-title"
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-ink-900 border border-ink-700 rounded shadow-lg max-w-md w-full p-5 text-steel-100"
          >
            <h3 id="suggestion-title" className="font-semibold text-lg mb-2 inline-flex items-center gap-2">
              <Sparkles aria-hidden className="w-5 h-5 text-amber-500" strokeWidth={1.75} />
              <span>Créer une règle d'équivalence ?</span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Tu as corrigé <code className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">{suggestion.skuInitial}</code>{' '}
              — {' '}
              <code className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">{suggestion.skuCorrige}</code>{' '}
              <strong>{suggestion.nbOccurrences} fois</strong> ces 30 derniers jours.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Créer une règle d'équivalence évitera cette correction à l'avenir — le Matcher appliquera automatiquement la substitution.
            </p>
            {error && (
              <div className="bg-red-950/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{error}</div>
            )}
            {done ? (
              <div className="bg-green-50 text-green-700 px-3 py-2 rounded text-sm text-center inline-flex items-center justify-center gap-1.5">
                <Check aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                <span>Règle créée</span>
              </div>
            ) : (
              <div className="flex gap-2 justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-1.5 rounded text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Plus tard
                </button>
                <button
                  onClick={createRule}
                  disabled={submitting}
                  className="bg-amber-500 hover:bg-amber-400 text-ink-950 px-4 py-1.5 rounded text-sm disabled:opacity-50"
                >
                  {submitting ? 'Création…' : 'Créer la règle'}
                </button>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ============================================================
// Comments thread
// ============================================================

type Comment = {
  id: string;
  tenderLineId: string;
  authorEmail: string;
  body: string;
  createdAt: number;
};

function CommentThread({
  tenderId,
  lineId,
  onChange,
}: {
  tenderId: string;
  lineId: string;
  onChange: () => Promise<unknown>;
}) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function refresh() {
    api
      .get<{ items: Comment[] }>(`/api/tenders/${tenderId}/lines/${lineId}/comments`)
      .then((d) => setComments(d.items ?? []))
      .catch(() => setComments([]));
  }

  useEffect(refresh, [tenderId, lineId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/api/tenders/${tenderId}/lines/${lineId}/comments`, { body });
      setBody('');
      refresh();
      void onChange();
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(commentId: string) {
    try {
      await api.delete(`/api/tenders/${tenderId}/lines/${lineId}/comments/${commentId}`);
      refresh();
      void onChange();
    } catch {
      // silent
    }
  }

  return (
    <div className="max-w-3xl">
      {comments.length === 0 ? (
        <p className="text-xs text-gray-500 mb-2">Aucun commentaire.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {comments.map((c) => (
            <li key={c.id} className="bg-white rounded p-2 text-sm border">
              <div className="flex items-center justify-between">
                <span className="font-medium text-xs text-gray-700">{c.authorEmail}</span>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span>{new Date(c.createdAt).toLocaleString('fr-FR')}</span>
                  <button
                    onClick={() => remove(c.id)}
                    className="text-red-500 hover:text-red-700 inline-flex items-center"
                    title="Supprimer"
                    aria-label="Supprimer"
                  >
                    <Trash2 aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
              <div className="mt-1 whitespace-pre-wrap">{renderMentionsInText(c.body)}</div>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="flex gap-2">
        <MentionAutocomplete
          value={body}
          onChange={setBody}
          placeholder="Ajouter un commentaire… (tape @ pour mentionner)"
          maxLength={4000}
          className="w-full border border-ink-700 rounded px-2 py-1 text-sm bg-ink-900 text-steel-100"
        />
        <button
          type="submit"
          disabled={submitting || !body.trim()}
          className="bg-primary-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
        >
          {submitting ? '…' : 'Poster'}
        </button>
      </form>
    </div>
  );
}

/**
 * Affiche le body d'un commentaire en highlightant les @mentions.
 */
function renderMentionsInText(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /(?:^|[\s,;:!?(\[])(@"[^"]+"|@[a-zA-Z0-9._@-]+)/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) != null) {
    const mentionStart = m.index + m[0].length - m[1]!.length;
    if (mentionStart > lastIdx) parts.push(text.slice(lastIdx, mentionStart));
    parts.push(
      <span
        key={key++}
        className="bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 px-1 rounded font-medium"
      >
        {m[1]}
      </span>,
    );
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

// ============================================================
// Atoms
// ============================================================

function ProposalRationale({ line }: { line: TenderLine }) {
  if (!line.skuPropose && !line.justification && !line.parametricDecision) return null;

  const decision = line.parametricDecision;
  const specs = line.specsExtraites ?? {};
  const specParts = Object.entries(specs)
    .filter(([, v]) => v != null && v !== '' && (!Array.isArray(v) || v.length > 0))
    .slice(0, 6)
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(',') : String(v)}`);
  const reasons: string[] = [];
  if (decision?.familyKey) reasons.push(`famille ${decision.familyKey}`);
  if (decision?.matchedColumnKey) reasons.push(`variante ${decision.matchedColumnKey}`);
  if (decision && decision.paramValueRequired > 0) {
    reasons.push(`besoin ${decision.paramValueRequired}, seuil catalogue ${decision.paramValueSelected ?? '?'}`);
  }
  if (specParts.length > 0) reasons.push(`specs AO: ${specParts.join(' · ')}`);

  const raw = line.justification ?? '';
  const isNoMatch = /aucun produit|aucune correspondance/i.test(raw);
  const isTechError = /erreur interne|internal error/i.test(raw);
  const cls = isTechError
    ? 'border-red-500 bg-red-900/20 text-red-300'
    : isNoMatch
      ? 'border-amber-500 bg-amber-900/15 text-amber-300'
      : 'border-ink-700 bg-ink-900/50 text-steel-300';

  return (
    <details className={`mt-2 border-l-2 ${cls} rounded-sm`}>
      <summary className="cursor-pointer px-2 py-1 text-2xs font-mono uppercase tracking-widest hover:text-amber-300">
        pourquoi cette proposition
      </summary>
      <div className="px-2 pb-2 text-2xs leading-snug space-y-2">
        {line.skuPropose && (
          <div>
            <span className="text-steel-500">SKU propose</span>{' '}
            <span className="font-mono text-amber-400">{line.skuPropose}</span>
          </div>
        )}
        {reasons.length > 0 && (
          <ul className="list-disc list-inside space-y-0.5">
            {reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        {raw && <div className="whitespace-pre-wrap">{raw}</div>}
        {line.alternatives && line.alternatives.length > 0 && (
          <div>
            <div className="text-steel-500 mb-1">alternatives non retenues</div>
            <ul className="space-y-0.5">
              {line.alternatives.slice(0, 3).map((alt) => (
                <li key={alt.sku}>
                  <span className="font-mono text-steel-100">{alt.sku}</span>{' '}
                  <span className="text-steel-500">{alt.raisonNonRetenu}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

function ParametricBadge({ decision }: { decision: NonNullable<TenderLine['parametricDecision']> }) {
  const calcs = Object.entries(decision.calculationsApplied);
  const hasCalcs = calcs.length > 0;
  const hasWarnings = decision.warnings.length > 0;
  return (
    <details className="mt-2 text-2xs">
      <summary className="cursor-pointer font-mono uppercase tracking-widest text-amber-500 hover:text-amber-400 inline-flex items-center gap-1.5">
        <Cog aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
        <span>{decision.familyKey}</span>
        {decision.matchedColumnKey && <span className="text-steel-500"> · {decision.matchedColumnKey}</span>}
        {decision.paramValueRequired > 0 && (
          <span className="text-steel-500"> · {decision.paramValueRequired}→{decision.paramValueSelected ?? '?'}</span>
        )}
      </summary>
      <div className="mt-2 ml-3 space-y-2 font-mono">
        <div>
          <span className="text-steel-500">family</span>{' '}
          <span className="text-amber-500">{decision.familyKey}</span>
        </div>
        {decision.matchedColumnKey && (
          <div>
            <span className="text-steel-500">variant</span>{' '}
            <span className="text-steel-300">{decision.matchedColumnKey}</span>
          </div>
        )}
        <div>
          <span className="text-steel-500">param</span>{' '}
          <span className="text-steel-300">required={decision.paramValueRequired}</span>
          {decision.paramValueSelected != null && (
            <> <span className="text-steel-500">·</span> <span className="text-emerald-400">selected={decision.paramValueSelected}</span></>
          )}
        </div>
        {hasCalcs && (
          <div>
            <div className="text-steel-500 mb-1">calculations</div>
            <ul className="ml-2 space-y-0.5">
              {calcs.map(([k, v]) => (
                <li key={k}>
                  <span className="text-amber-500">{k}</span> = <span className="text-steel-300">{String(v.result)}</span>
                  <span className="text-steel-700 ml-2">λ {v.expression}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {hasWarnings && (
          <div className="text-red-400">
            <div className="mb-1">⚠ warnings</div>
            <ul className="ml-2 space-y-0.5">
              {decision.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

function ConfidenceBadge({ level, score }: { level: ConfidenceLevel; score: number | null }) {
  const colors: Record<ConfidenceLevel, string> = {
    haute: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    moyenne: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    basse: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  // On n'affiche le score que s'il est > 0 (sinon c'est juste du bruit visuel).
  const showScore = score != null && score > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium ${colors[level]}`}
    >
      <span>{level}</span>
      {showScore && (
        <span className="font-mono text-2xs opacity-70">{Math.round(score * 100)}%</span>
      )}
    </span>
  );
}

/**
 * ARG B-07 — pastille C_info colorée selon le score :
 *   - rouge < 0.6  → INFO_LOW_CONFIDENCE
 *   - jaune 0.6–0.8 → UNCERTAIN_SUCCESS (zone basse)
 *   - vert  ≥ 0.8  → UNCERTAIN_SUCCESS (zone haute) / CONFIRMED
 *
 * Cohérent avec la sémantique ARG : c_info ne traduit que la confiance ARG,
 * il NE BASCULE PAS automatiquement vers CONFIRMED — ça reste un clic user.
 */
function ArgConfidenceDot({ cInfo }: { cInfo: number }) {
  const color =
    cInfo < 0.6
      ? 'bg-red-500/80 text-red-100 border-red-700'
      : cInfo < 0.8
        ? 'bg-amber-500/70 text-amber-50 border-amber-600'
        : 'bg-emerald-500/80 text-emerald-50 border-emerald-700';
  return (
    <Tooltip label={`C_info ARG = ${cInfo.toFixed(2)}`} side="top">
      <span
        className={`inline-flex items-center justify-center w-6 h-5 rounded-full border text-2xs font-mono font-semibold ${color}`}
        aria-label={`Score ARG ${cInfo.toFixed(2)}`}
      >
        {cInfo.toFixed(2).slice(2) /* "85" pour 0.85 */}
      </span>
    </Tooltip>
  );
}

/**
 * ARG B-07 — badge texte du `info_outcome_status`. Cohabite avec ReviewBadge
 * (statut legacy de la revue humaine) et ArgConfidenceDot (score numérique).
 *
 * Les 3 statuts ARG visualisables :
 *   INFO_LOW_CONFIDENCE     — rouge,  c_info < 0.6
 *   INFO_UNCERTAIN_SUCCESS  — jaune,  c_info ≥ 0.6 sans clic user
 *   INFO_CONFIRMED_SUCCESS  — vert,   user a cliqué "vérifié"
 */
function InfoOutcomeBadge({
  status,
}: {
  status: 'INFO_LOW_CONFIDENCE' | 'INFO_UNCERTAIN_SUCCESS' | 'INFO_CONFIRMED_SUCCESS';
}) {
  const config: Record<typeof status, { label: string; cls: string }> = {
    INFO_LOW_CONFIDENCE: {
      label: 'low',
      cls: 'bg-red-500/15 text-red-300 border-red-500/30',
    },
    INFO_UNCERTAIN_SUCCESS: {
      label: 'uncertain',
      cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    },
    INFO_CONFIRMED_SUCCESS: {
      label: '✓ verified',
      cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    },
  };
  const { label, cls } = config[status];
  return (
    <span
      className={`text-2xs px-1.5 py-0 rounded border font-mono ${cls}`}
      aria-label={`ARG status ${status}`}
    >
      {label}
    </span>
  );
}

function ReviewBadge({ status }: { status: ReviewStatus }) {
  const colors: Record<ReviewStatus, string> = {
    pending: 'bg-ink-800 text-steel-300 border-ink-700',
    accepted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    corrected: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
    waiting_client: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  };
  const labels: Record<ReviewStatus, string> = {
    pending: 'à revoir',
    accepted: 'accepté',
    corrected: 'corrigé',
    rejected: 'rejeté',
    waiting_client: 'attente client',
  };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded border font-medium ${colors[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function ActionBtn({
  icon: Icon,
  title,
  busy,
  onClick,
  color,
}: {
  icon: import('lucide-react').LucideIcon;
  title: string;
  busy: boolean;
  onClick: () => void;
  color: 'green' | 'blue' | 'red' | 'purple';
}) {
  const colors = {
    green: 'hover:bg-emerald-500/15 text-emerald-400',
    blue: 'hover:bg-blue-500/15 text-blue-400',
    red: 'hover:bg-red-500/15 text-red-400',
    purple: 'hover:bg-purple-500/15 text-purple-400',
  };
  // Tooltip natif (title) plutôt que le Tooltip custom : sur ces boutons étroits
  // et alignés, le tooltip absolu se chevauchait avec les voisins et restait
  // collé après un clic (focus-within). Le natif n'affiche que le bouton survolé.
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={title}
      aria-label={title}
      className={`w-7 h-7 rounded flex items-center justify-center ${colors[color]} disabled:opacity-50`}
    >
      {busy ? '…' : <Icon aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />}
    </button>
  );
}

type RunnerArtifactMeta = {
  id: string;
  kind: string;
  r2Key: string;
  mimeType: string;
  sizeBytes: number;
  updatedAt: number;
};


type AttemptMeta = {
  id: string;
  seq: number;
  statut: string;
  isCurrent: boolean;
  catalogueVersionId: string | null;
  nbLignes: number | null;
  startedAt: number;
  completedAt: number | null;
  createdBy: string | null;
  note: string | null;
};

type AnswerMeta = {
  id: string;
  attemptId: string;
  questionId: string | null;
  code: string | null;
  path: string | null;
  table: string | null;
  itemId: string | null;
  answerText: string | null;
  refSku: string | null;
  createdAt: number;
  createdBy: string | null;
};

type AttemptDiff = {
  a: { id: string; seq: number; catalogueVersionId: string | null };
  b: { id: string; seq: number; catalogueVersionId: string | null };
  catalogueChanged: boolean;
  diff: {
    added: Array<Record<string, unknown>>;
    removed: Array<Record<string, unknown>>;
    changed: Array<{ key: string; before: Record<string, unknown>; after: Record<string, unknown> }>;
    unchanged: number;
  };
};

function fmtAttemptDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('fr-FR');
  } catch {
    return String(ts);
  }
}

// Historique des tentatives de traitement : liste, comparaison (diff) entre deux
// tentatives (ex. avant/après changement de catalogue), et journal des réponses
// humaines (UID + date + auteur). Revue admin incluse.
type AttemptLine = {
  item_id?: string;
  designation?: string;
  ref?: string;
  quantity?: number | string;
  status?: string;
};

function AttemptsHistoryPanel({ tenderId }: { tenderId: string }) {
  const [attempts, setAttempts] = useState<AttemptMeta[]>([]);
  const [answers, setAnswers] = useState<AnswerMeta[]>([]);
  const [aId, setAId] = useState('');
  const [bId, setBId] = useState('');
  const [diff, setDiff] = useState<AttemptDiff | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Consultation par version : lignes finales chargées à la demande + erreurs
  // par ligne d'historique (Excel/lignes parfois absents sur une tentative qui
  // n'a pas atteint l'étape correspondante).
  const [linesByAttempt, setLinesByAttempt] = useState<Record<string, AttemptLine[] | null>>({});
  const [openLines, setOpenLines] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const { confirm, dialog } = useConfirm();

  function loadAttempts() {
    api
      .get<{ items: AttemptMeta[] }>(`/api/tenders/${tenderId}/attempts`)
      .then((d) => {
        const items = d.items ?? [];
        setAttempts(items);
        if (items.length >= 2) {
          setBId(items[0].id);
          setAId(items[1].id);
        } else if (items.length === 1) {
          setBId(items[0].id);
          setAId(items[0].id);
        }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Erreur tentatives'));
  }

  async function deleteAttempt(a: AttemptMeta) {
    const ok = await confirm({
      title: `Supprimer la tentative #${a.seq} ?`,
      message: 'Cette version et ses artefacts seront définitivement supprimés. Les autres versions sont conservées.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
    });
    if (!ok) return;
    setRowBusy(`del:${a.id}`);
    setErr(null);
    try {
      await api.delete(`/api/tenders/${tenderId}/attempts/${a.id}`);
      loadAttempts();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur suppression');
    } finally {
      setRowBusy(null);
    }
  }

  async function pruneEmpty() {
    const ok = await confirm({
      title: 'Nettoyer les tentatives sans résultat ?',
      message: 'Supprime toutes les versions échouées (sans lignes finales) sauf la version courante.',
      confirmLabel: 'Nettoyer',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ deleted: number }>(`/api/tenders/${tenderId}/attempts/prune-empty`, {});
      setErr(r.deleted > 0 ? null : null);
      loadAttempts();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur nettoyage');
    } finally {
      setBusy(false);
    }
  }

  async function downloadAttemptExcel(a: AttemptMeta) {
    setRowErr((m) => ({ ...m, [a.id]: '' }));
    setRowBusy(`excel:${a.id}`);
    try {
      await downloadFile(
        `/api/tenders/${tenderId}/attempts/${a.id}/artifacts/excel`,
        `reponse_AO_v${a.seq}.xlsx`,
      );
    } catch (e) {
      setRowErr((m) => ({ ...m, [a.id]: e instanceof Error ? e.message : 'Excel indisponible pour cette version' }));
    } finally {
      setRowBusy(null);
    }
  }

  async function toggleAttemptLines(a: AttemptMeta) {
    if (openLines === a.id) {
      setOpenLines(null);
      return;
    }
    setOpenLines(a.id);
    if (linesByAttempt[a.id] !== undefined) return; // déjà chargé
    setRowBusy(`lines:${a.id}`);
    setRowErr((m) => ({ ...m, [a.id]: '' }));
    try {
      const r = await api.get<{ content?: { lines?: AttemptLine[] } }>(
        `/api/tenders/${tenderId}/attempts/${a.id}/artifacts/final_lines`,
      );
      setLinesByAttempt((m) => ({ ...m, [a.id]: Array.isArray(r.content?.lines) ? r.content!.lines! : [] }));
    } catch (e) {
      setLinesByAttempt((m) => ({ ...m, [a.id]: null }));
      setRowErr((m) => ({ ...m, [a.id]: e instanceof Error ? e.message : 'Lignes indisponibles pour cette version' }));
    } finally {
      setRowBusy(null);
    }
  }

  useEffect(() => {
    loadAttempts();
    api
      .get<{ items: AnswerMeta[] }>(`/api/tenders/${tenderId}/answers`)
      .then((d) => setAnswers(d.items ?? []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId]);

  async function compare() {
    if (!aId || !bId || aId === bId) {
      setErr('Choisis deux tentatives differentes.');
      return;
    }
    setBusy(true);
    setErr(null);
    setDiff(null);
    try {
      setDiff(await api.get<AttemptDiff>(`/api/tenders/${tenderId}/attempts/${aId}/diff/${bId}`));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur diff');
    } finally {
      setBusy(false);
    }
  }

  if (attempts.length === 0) return null;

  return (
    <div className="tc-panel p-4 mb-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="tc-label">historique des tentatives ({attempts.length})</div>
        {attempts.some((a) => !a.isCurrent) && (
          <button
            type="button"
            onClick={pruneEmpty}
            disabled={busy}
            title="Supprime les versions échouées (sans lignes finales), sauf la version courante"
            className="tc-btn-ghost text-2xs"
          >
            Nettoyer les tentatives sans résultat
          </button>
        )}
      </div>
      {err && <div className="bg-red-950/30 text-red-300 px-3 py-2 mb-3 text-xs font-mono">{err}</div>}

      <div className="space-y-1 mb-4">
        {attempts.map((a) => (
          <div key={a.id} className="border border-ink-700 bg-ink-950/50">
            <div className="flex flex-wrap items-center gap-3 text-xs font-mono px-2 py-1">
              <span className="text-steel-200">#{a.seq}</span>
              <span className={a.isCurrent ? 'text-emerald-300' : 'text-steel-400'}>
                {a.statut}{a.isCurrent ? ' (courante)' : ''}
              </span>
              <span className="text-steel-500">{fmtAttemptDate(a.startedAt)}</span>
              <span className="text-steel-500">cat: {a.catalogueVersionId ?? '—'}</span>
              {a.nbLignes != null && <span className="text-steel-500">{a.nbLignes} ligne(s)</span>}
              <span className="text-steel-500">{answers.filter((x) => x.attemptId === a.id).length} reponse(s)</span>
              <span className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={() => toggleAttemptLines(a)}
                  disabled={rowBusy === `lines:${a.id}`}
                  className="tc-btn-secondary text-2xs"
                >
                  {openLines === a.id ? 'Masquer' : 'Voir les lignes'}
                </button>
                <button
                  type="button"
                  onClick={() => downloadAttemptExcel(a)}
                  disabled={rowBusy === `excel:${a.id}`}
                  className="tc-btn-secondary text-2xs"
                >
                  {rowBusy === `excel:${a.id}` ? '…' : 'Excel'}
                </button>
                {!a.isCurrent && (
                  <button
                    type="button"
                    onClick={() => deleteAttempt(a)}
                    disabled={rowBusy === `del:${a.id}`}
                    title="Supprimer cette version"
                    className="tc-btn-ghost text-2xs text-red-300 hover:text-red-200"
                  >
                    {rowBusy === `del:${a.id}` ? '…' : 'Supprimer'}
                  </button>
                )}
              </span>
            </div>
            {rowErr[a.id] && (
              <div className="text-red-300 text-2xs font-mono px-2 pb-1">{rowErr[a.id]}</div>
            )}
            {openLines === a.id && (
              <div className="border-t border-ink-700 px-2 py-2">
                {rowBusy === `lines:${a.id}` ? (
                  <div className="text-steel-500 text-2xs font-mono">Chargement…</div>
                ) : linesByAttempt[a.id] && linesByAttempt[a.id]!.length > 0 ? (
                  <div className="space-y-0.5 max-h-72 overflow-auto">
                    {linesByAttempt[a.id]!.map((l, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2 text-2xs font-mono">
                        <span className="text-steel-200 shrink-0">{String(l.ref ?? '—')}</span>
                        <span className="text-steel-400 flex-1 min-w-0 truncate">{String(l.designation ?? l.item_id ?? '')}</span>
                        {l.quantity != null && <span className="text-steel-500 shrink-0">x{String(l.quantity)}</span>}
                        {l.status && <span className="text-steel-600 shrink-0">{String(l.status)}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-steel-500 text-2xs font-mono">Aucune ligne pour cette version.</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {attempts.length >= 2 && (
        <div className="mb-4">
          <div className="tc-label mb-2">comparer deux tentatives</div>
          <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
            <select value={aId} onChange={(e) => setAId(e.target.value)} className="tc-input text-xs">
              {attempts.map((a) => (
                <option key={a.id} value={a.id}>#{a.seq} ({fmtAttemptDate(a.startedAt)})</option>
              ))}
            </select>
            <span className="text-steel-500">vs</span>
            <select value={bId} onChange={(e) => setBId(e.target.value)} className="tc-input text-xs">
              {attempts.map((a) => (
                <option key={a.id} value={a.id}>#{a.seq} ({fmtAttemptDate(a.startedAt)})</option>
              ))}
            </select>
            <button type="button" onClick={compare} disabled={busy} className="tc-btn-secondary text-2xs">
              Comparer
            </button>
          </div>

          {diff && (
            <div className="text-xs font-mono border border-ink-700 bg-ink-950/50 p-3 space-y-1">
              {diff.catalogueChanged && (
                <div className="text-amber-300">⚠ catalogue different entre les deux tentatives</div>
              )}
              <div className="text-steel-400">
                #{diff.a.seq} → #{diff.b.seq} · {diff.diff.changed.length} modifiee(s), {diff.diff.added.length} ajoutee(s), {diff.diff.removed.length} retiree(s), {diff.diff.unchanged} inchangee(s)
              </div>
              {diff.diff.changed.map((c) => (
                <div key={c.key} className="text-amber-200">
                  ~ {c.key}: ref {String((c.before as Record<string, unknown>).ref ?? '—')} → {String((c.after as Record<string, unknown>).ref ?? '—')} · qte {String((c.before as Record<string, unknown>).quantity ?? '—')} → {String((c.after as Record<string, unknown>).quantity ?? '—')}
                </div>
              ))}
              {diff.diff.added.map((l, i) => (
                <div key={`add_${i}`} className="text-emerald-300">+ {String((l as Record<string, unknown>).key)}: {String((l as Record<string, unknown>).ref ?? '—')}</div>
              ))}
              {diff.diff.removed.map((l, i) => (
                <div key={`rm_${i}`} className="text-red-300">− {String((l as Record<string, unknown>).key)}: {String((l as Record<string, unknown>).ref ?? '—')}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {answers.length > 0 && (
        <div>
          <div className="tc-label mb-2">reponses humaines ({answers.length})</div>
          <div className="space-y-1 max-h-64 overflow-auto">
            {answers.map((ans) => {
              const att = attempts.find((a) => a.id === ans.attemptId);
              return (
                <div key={ans.id} className="text-xs font-mono border border-ink-700 bg-ink-950/50 px-2 py-1">
                  <span className="text-steel-500">#{att?.seq ?? '?'}</span>{' '}
                  <span className="text-steel-500">{fmtAttemptDate(ans.createdAt)}</span>{' '}
                  <span className="text-steel-300">{ans.table ?? ans.code ?? ans.questionId ?? ''}</span>
                  {ans.refSku && <span className="text-emerald-300"> → {ans.refSku}</span>}
                  {ans.answerText && <span className="text-steel-400"> · {ans.answerText}</span>}
                  {ans.createdBy && <span className="text-steel-600"> ({ans.createdBy})</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {dialog}
    </div>
  );
}

function RunnerArtifactsPanel({
  tenderId,
  status,
  onChange,
  onClose,
}: {
  tenderId: string;
  status: TenderStatus;
  onChange: () => Promise<void>;
  onClose: () => void;
}) {
  const editableKinds = ['params', 'references', 'final_lines'];
  const binaryKinds = ['references_review_excel', 'excel'];
  const diagnosticKinds = ['references_review_excel', 'guardrails_report', 'requirements_plan', 'tender_impact', 'tender_parsed_lines'];
  const [items, setItems] = useState<RunnerArtifactMeta[]>([]);
  const [selectedKind, setSelectedKind] = useState('references');
  const [draft, setDraft] = useState('');
  const [questions, setQuestions] = useState<RunnerQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, { answer: string; ref: string; qty?: string; promote?: boolean; ruleNote?: string; createSku?: boolean; skuDesignation?: string }>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Questions de paramètres dépliées par défaut.
  const [questionsOpen, setQuestionsOpen] = useState(true);
  const canResume = status === 'awaiting_params_review'
    || status === 'awaiting_references_review'
    || status === 'awaiting_final_review';

  function refreshArtifacts() {
    api
      .get<{ items: RunnerArtifactMeta[] }>(`/api/tenders/${tenderId}/artifacts`)
      .then((d) => {
        setItems(d.items ?? []);
        const available = d.items?.find((i) => diagnosticKinds.includes(i.kind))
          ?? d.items?.find((i) => editableKinds.includes(i.kind));
        if (available && !d.items.some((i) => i.kind === selectedKind)) {
          setSelectedKind(available.kind);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erreur artefacts'));
  }

  async function refreshQuestions() {
    const [params, references, guardrails, finalLines] = await Promise.all([
      fetchArtifactObject(tenderId, 'params'),
      fetchArtifactObject(tenderId, 'references'),
      fetchArtifactObject(tenderId, 'guardrails_report'),
      fetchArtifactObject(tenderId, 'final_lines'),
    ]);
    setQuestions(extractRunnerQuestions({ params, references, guardrails, finalLines }));
  }

  useEffect(refreshArtifacts, [tenderId]);
  useEffect(() => {
    refreshQuestions().catch(() => setQuestions([]));
  }, [tenderId, status]);

  useEffect(() => {
    setError(null);
    setMessage(null);
    if (binaryKinds.includes(selectedKind)) {
      setDraft('');
      return;
    }
    api
      .get<{ kind: string; content: unknown }>(`/api/tenders/${tenderId}/artifacts/${selectedKind}`)
      .then((d) => setDraft(JSON.stringify(d.content, null, 2)))
      .catch(() => setDraft(''));
  }, [tenderId, selectedKind]);

  async function save() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const parsed = JSON.parse(draft);
      await api.put(`/api/tenders/${tenderId}/artifacts/${selectedKind}`, { content: parsed });
      setMessage(`${selectedKind} sauvegarde`);
      refreshArtifacts();
      await refreshQuestions();
    } catch (err) {
      setError(err instanceof SyntaxError ? 'JSON invalide' : err instanceof Error ? err.message : 'Erreur sauvegarde');
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post(`/api/tenders/${tenderId}/resume-runner`);
      setMessage('Recalcul lancé avec tes corrections — les lignes vont se mettre à jour…');
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur reprise runner');
    } finally {
      setBusy(false);
    }
  }

  async function openReviewViewer(provider: 'office' | 'google') {
    const popup = window.open('about:blank', '_blank');
    if (popup) {
      popup.document.title = provider === 'google' ? 'Creation Google Sheet' : 'Ouverture Office 365';
      popup.document.body.innerHTML = '<div style="font-family:system-ui;padding:24px;color:#111">Creation du fichier en cours...</div>';
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      let url: string;
      if (provider === 'google') {
        const sheet = await api.post<{ url?: string; authUrl?: string; requiresAuth?: boolean; id?: string; name?: string }>(
          `/api/tenders/${tenderId}/artifacts/references_review_excel/create-google-sheet`,
        );
        url = sheet.authUrl ?? sheet.url ?? '';
        if (!url) throw new Error('Google Sheets n a pas retourne de lien.');
      } else {
        const links = await api.post<{
          officeUrl: string;
          googleUrl: string;
          expiresAt: number;
        }>(`/api/tenders/${tenderId}/artifacts/references_review_excel/viewer-links`);
        url = links.officeUrl;
      }
      if (popup) {
        popup.opener = null;
        popup.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setMessage(
        provider === 'office'
          ? 'Lien Office 365 genere pour 24h'
          : 'Google autorisation ouverte',
      );
    } catch (err) {
      if (popup) popup.close();
      setError(err instanceof Error ? err.message : 'Erreur ouverture viewer');
    } finally {
      setBusy(false);
    }
  }

  async function answerQuestion(question: RunnerQuestion) {
    const value = answers[question.id] ?? { answer: '', ref: '' };
    if (!value.answer.trim() && !value.ref.trim() && !(value.qty ?? '').trim()) {
      setError('Ajoute une reponse, une reference/SKU ou une quantite avant de sauvegarder.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      // Réponse durable (UID + date + auteur) côté serveur ; le backend
      // l'enregistre dans tender_answers ET l'applique aux references courantes.
      // Quand c'est la dernière question, le backend relance le pipeline
      // automatiquement (resumed=true) — on l'indique et on rafraîchit le tender.
      const res = await api.post<{ remaining: number; ruleApplied?: boolean; ruleConflict?: string | null; skuUnknown?: boolean }>(
        `/api/tenders/${tenderId}/answers`, {
          questionId: question.id,
          source: question.source,
          path: question.path ?? null,
          code: question.code ?? null,
          table: question.table ?? null,
          itemId: question.itemId ?? null,
          lotId: question.lotId ?? null,
          message: question.message ?? null,
          answer: value.answer,
          ref: value.ref,
          qty: (value.qty ?? '').trim() || null,
          promoteToRule: value.promote === true,
          ruleNote: value.ruleNote ?? null,
          createSku: value.createSku === true,
          skuDesignation: value.skuDesignation ?? null,
        });
      const promoted = value.promote === true;
      const rulePrefix = !promoted
        ? 'Réponse enregistrée. '
        : res.skuUnknown && value.createSku !== true
          ? `⚠ Le SKU « ${value.ref} » n'existe pas dans GS.DATA. Corrige-le, ou coche « SKU absent → créer » si c'est un nouveau produit. `
          : res.ruleConflict
            ? `⚠ Conflit : ta règle en contredit une autre (${res.ruleConflict}). Tracée pour arbitrage en admin. `
            : value.createSku === true
              ? (res.ruleApplied ? '✅ Nouveau SKU créé dans GS.DATA + règle publiée. ' : 'Nouveau SKU proposé à la création dans GS.DATA (revue admin). ')
              : res.ruleApplied
                ? '✅ Règle envoyée directement à GS.DATA (publiée) + tracée. '
                : 'Correction enregistrée + proposée comme règle générale (revue admin). ';
      setMessage(rulePrefix
        + (res.remaining > 0
          ? `Il reste ${res.remaining} question(s). Clique « Recalculer » quand tu as tout corrigé.`
          : 'Tout est traité — clique « Recalculer » pour appliquer tes corrections.'));
      await refreshQuestions();
      if (selectedKind === 'references') {
        const refs = await fetchArtifactObject(tenderId, 'references');
        if (refs) setDraft(JSON.stringify(refs, null, 2));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur sauvegarde reponse');
    } finally {
      setBusy(false);
    }
  }

  // Le panneau ne porte QUE les questions de paramètres/périmètre (elles ne sont
  // pas des lignes du devis). Les points de niveau ligne (references/final_lines/
  // guardrails) sont désormais traités directement dans le tableau du devis.
  const paramQuestions = questions.filter((q) => q.source === 'params');

  return (
    <div className="tc-panel p-4 mb-4 border-amber-900/50">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="tc-label mb-1">artefacts TenderFlow</div>
          <div className="text-sm text-steel-300">
            Revue des references en Excel, puis reprise du pipeline apres correction des points bloquants.
          </div>
        </div>
        <button type="button" onClick={onClose} className="tc-btn-ghost text-2xs">
          Fermer
        </button>
      </div>

      {error && <div className="bg-red-950/30 text-red-300 px-3 py-2 mb-3 text-xs font-mono">{error}</div>}
      {message && <div className="bg-emerald-950/30 text-emerald-300 px-3 py-2 mb-3 text-xs font-mono">{message}</div>}

      {(paramQuestions.length > 0 || canResume) && (
        <div className="mb-4 border border-amber-900/60 bg-amber-950/20 p-3">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              {paramQuestions.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => setQuestionsOpen((o) => !o)}
                    className="tc-label mb-1 inline-flex items-center gap-1.5 hover:text-amber-300"
                    aria-expanded={questionsOpen}
                  >
                    <span>{questionsOpen ? '▾' : '▸'}</span>
                    <span>{paramQuestions.length} question(s) générale(s)</span>
                  </button>
                  <div className="text-sm text-amber-200">
                    Questions de <strong>paramètres / périmètre</strong> (ce ne sont pas des lignes du devis).
                    Réponds ici, puis « Recalculer ». Les corrections de <strong>lignes</strong> se font
                    directement dans le tableau du devis ci-dessous.
                  </div>
                </>
              ) : (
                <>
                  <div className="tc-label mb-1">points bloquants</div>
                  <div className="text-sm text-amber-200">
                    Aucune question de paramètre. Corrige les lignes en attention <strong>dans le tableau du
                    devis</strong> ci-dessous, puis « Recalculer avec mes corrections ».
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {items.some((i) => i.kind === 'references_review_excel') && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      downloadFile(`/api/tenders/${tenderId}/download?kind=references_review_excel`).catch((e) =>
                        setError(e instanceof Error ? e.message : 'Erreur download Excel'),
                      )
                    }
                    className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5"
                  >
                    <Download aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Excel revue
                  </button>
                  <button
                    type="button"
                    onClick={() => openReviewViewer('office')}
                    disabled={busy}
                    className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5"
                  >
                    <FileText aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Office 365
                  </button>
                  <button
                    type="button"
                    onClick={() => openReviewViewer('google')}
                    disabled={busy}
                    className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5"
                  >
                    <FileText aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Creer Google Sheet
                  </button>
                </>
              )}
              {paramQuestions.length > 0 && (
                <span className="tc-status tc-status-warn">{paramQuestions.length} question(s)</span>
              )}
            </div>
          </div>
          {paramQuestions.length > 0 && questionsOpen && (
            <div className="space-y-2">
              {paramQuestions.map((q) => {
                const value = answers[q.id] ?? { answer: '', ref: '' };
                return (
                  <div key={q.id} className="border border-ink-700 bg-ink-950/70 p-3">
                    <div className="font-mono text-2xs uppercase tracking-widest text-amber-300">
                      {q.code ?? 'paramètre'}
                    </div>
                    <div className="text-sm text-steel-100 mt-1">{q.title}</div>
                    {q.message && q.message !== q.title && (
                      <div className="text-xs text-steel-400 mt-1">{q.message}</div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_auto] gap-2 mt-2 items-end">
                      <label className="block">
                        <span className="tc-label block mb-1">réponse</span>
                        <textarea
                          value={value.answer}
                          onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: { ...value, answer: e.target.value } }))}
                          rows={2}
                          className="tc-input text-xs"
                          placeholder="Précision, choix technique, justification..."
                        />
                      </label>
                      <label className="block">
                        <span className="tc-label block mb-1">réf / valeur (option.)</span>
                        <input
                          value={value.ref}
                          onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: { ...value, ref: e.target.value } }))}
                          className="tc-input font-mono text-xs"
                          placeholder="optionnel"
                        />
                      </label>
                      <button type="button" onClick={() => answerQuestion(q)} disabled={busy} className="tc-btn-secondary text-2xs">
                        Sauver
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <details className="mb-3">
        <summary className="cursor-pointer text-2xs font-mono uppercase tracking-widest text-steel-500 hover:text-amber-400 select-none py-1">
          Édition avancée — artefacts (JSON / Excel de revue)
        </summary>
        <div className="mt-3">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {items.map((item) => (
          <button
            key={item.kind}
            type="button"
            onClick={() => setSelectedKind(item.kind)}
            className={`px-3 py-1.5 text-2xs font-mono uppercase tracking-widest border ${
              selectedKind === item.kind
                ? 'border-amber-500 bg-amber-500 text-ink-950'
                : 'border-ink-700 text-steel-400 hover:text-steel-100'
            }`}
          >
            {artifactLabel(item.kind)}
          </button>
        ))}
      </div>

      {binaryKinds.includes(selectedKind) ? (
        <div className="border border-ink-700 bg-ink-950 p-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm text-steel-100">{artifactLabel(selectedKind)}</div>
            <div className="text-xs text-steel-500 mt-1">
              Fichier tableur pret a ouvrir pour revue commerciale.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={() =>
                downloadFile(`/api/tenders/${tenderId}/download?kind=${selectedKind}`).catch((e) =>
                  setError(e instanceof Error ? e.message : 'Erreur download Excel'),
                )
              }
              className="tc-btn-primary text-2xs inline-flex items-center gap-1.5"
            >
              <Download aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
              Telecharger
            </button>
            {selectedKind === 'references_review_excel' && (
              <>
                <button
                  type="button"
                  onClick={() => openReviewViewer('office')}
                  disabled={busy}
                  className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5"
                >
                  <FileText aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Office 365
                </button>
                <button
                  type="button"
                  onClick={() => openReviewViewer('google')}
                  disabled={busy}
                  className="tc-btn-secondary text-2xs inline-flex items-center gap-1.5"
                >
                  <FileText aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Creer Google Sheet
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          readOnly={!editableKinds.includes(selectedKind)}
          rows={14}
          className={`w-full bg-ink-950 border border-ink-700 font-mono text-xs p-3 ${
            editableKinds.includes(selectedKind) ? 'text-steel-100' : 'text-steel-400'
          }`}
          spellCheck={false}
        />
      )}
      {!editableKinds.includes(selectedKind) && !binaryKinds.includes(selectedKind) && (
        <div className="text-2xs text-steel-600 font-mono mt-2">
          Lecture seule : cet artefact est regenere automatiquement par TenderFlow.
        </div>
      )}
          {editableKinds.includes(selectedKind) && (
            <div className="flex justify-end mt-2">
              <button type="button" onClick={save} disabled={busy} className="tc-btn-secondary text-2xs">
                Sauvegarder JSON
              </button>
            </div>
          )}
        </div>
      </details>

      <div className="flex items-center justify-between gap-2 mt-3">
        <div className="text-2xs text-steel-600 font-mono">
          {items.length} artefact(s) · statut {status}
        </div>
        <div className="flex items-center gap-2">
          {canResume && (
            <button type="button" onClick={resume} disabled={busy}
              title="Applique toutes tes réponses/corrections et relance le calcul (reviewer → lignes finales)"
              className="tc-btn-primary text-2xs inline-flex items-center gap-1.5">
              <Play aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
              {busy ? 'Recalcul…' : 'Recalculer avec mes corrections'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function artifactLabel(kind: string): string {
  if (kind === 'references_review_excel') return 'Excel revue references';
  if (kind === 'guardrails_report') return 'Guardrails';
  if (kind === 'requirements_plan') return 'Plan besoins';
  if (kind === 'tender_impact') return 'Impact AO';
  if (kind === 'tender_parsed_lines') return 'Lignes AO';
  if (kind === 'final_lines') return 'Lignes finales';
  return kind;
}

async function fetchArtifactObject(tenderId: string, kind: string): Promise<Record<string, unknown> | null> {
  try {
    const response = await api.get<{ kind: string; content: unknown }>(`/api/tenders/${tenderId}/artifacts/${kind}`);
    return isRecord(response.content) ? response.content : null;
  } catch {
    return null;
  }
}


function FilterChip({
  label,
  count,
  active,
  onClick,
  color,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  color?: 'green' | 'orange' | 'red';
}) {
  // Theme sombre, cohérent avec le reste de l'app.
  const colorActive: Record<NonNullable<typeof color>, string> = {
    green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/50',
    orange: 'bg-amber-500/15 text-amber-300 border-amber-500/50',
    red: 'bg-red-500/15 text-red-300 border-red-500/50',
  };
  const colorIdle: Record<NonNullable<typeof color>, string> = {
    green: 'text-emerald-400 hover:bg-emerald-500/10 border-transparent',
    orange: 'text-amber-400 hover:bg-amber-500/10 border-transparent',
    red: 'text-red-400 hover:bg-red-500/10 border-transparent',
  };
  const cls = color
    ? active
      ? colorActive[color]
      : colorIdle[color]
    : active
      ? 'bg-brand-500/15 text-brand-300 border-brand-500/50'
      : 'text-steel-300 hover:bg-ink-800 border-transparent';
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1.5 text-xs font-medium border rounded inline-flex items-center gap-1.5 transition-colors ${cls}`}
    >
      <span>{label}</span>
      {count !== undefined && (
        <span className="font-mono text-2xs opacity-70">{count}</span>
      )}
    </button>
  );
}
